import useSidebarState from '../hooks/useSidebarState';
import { uiText, useUiLanguage } from '../utils/uiTranslation';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ref, get, set, push, update, onValue, remove, query as dbQuery, orderByChild, equalTo } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { database, storage } from '../firebase';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import '../styles/tutorial.css';
import './Onboarding.css';
import Sidebar from './Sidebar';
import FeatureGate from './Trial/FeatureGate';
import { flushSync } from 'react-dom';
import { buildEmployeeRecordFromHire } from '../utils/employeeRecords';
import nationalizationService, {
  addNationalizationLifecycleEventToUpdates,
  announceNationalizationWorkforceChange,
  startNationalizationLifecycleReplay
} from '../services/nationalizationService';
import { normalizeAnalyticsSource } from '../utils/analyticsEntityFields';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import DocumentManager from './MyTeam/EmployeeDrawer/DocumentManager';
import JSZip from 'jszip';
import useTeamAccessProfile from '../hooks/useTeamAccessProfile';

// New Workflow Components
import {
  OnboardingWorkflowView,
  OnboardingStageTimeline,
  AddAutomatedTaskModal,
  AddHumanTaskModal,
  ExternalContactsSection,
  FormBuilderModal
} from './Onboarding/index';
import OnboardingQuillEditor from './Onboarding/OnboardingQuillEditor';
import ExternalContactsModal from './Onboarding/ExternalContactsModal';
import TemplateManagerModal from './Onboarding/TemplateManagerModal';
import HireTaskWorkspace from './Onboarding/TaskWorkspaceView';

const cloneData = (value) => JSON.parse(JSON.stringify(value));
const NEW_HIRE_AUTOMATION_TYPES = new Set(['document_request', 'document_send', 'form_collect', 'meeting_schedule']);
const PURE_AUTOMATION_TYPES = new Set(['scheduled_email']);
const INLINE_MENU_WIDTH = 188;
const INLINE_MENU_HEIGHT = 150;

const localIsoDate = (value = new Date()) => {
  const dateValue = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dateValue.getTime())) return '';
  const year = dateValue.getFullYear();
  const month = String(dateValue.getMonth() + 1).padStart(2, '0');
  const day = String(dateValue.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getFixedInlineMenuPosition = (anchorEl) => {
  if (!anchorEl || typeof window === 'undefined') return { top: 0, left: 0 };

  const rect = anchorEl.getBoundingClientRect();
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  const left = Math.min(
    Math.max(8, rect.right - INLINE_MENU_WIDTH),
    Math.max(8, viewportWidth - INLINE_MENU_WIDTH - 8)
  );
  const belowTop = rect.bottom + 6;
  const top = belowTop + INLINE_MENU_HEIGHT > viewportHeight
    ? Math.max(8, rect.top - INLINE_MENU_HEIGHT - 6)
    : belowTop;

  return { top, left };
};

const extractRequestedDocuments = (task = {}) => {
  if (Array.isArray(task.requiredDocuments) && task.requiredDocuments.length > 0) {
    return task.requiredDocuments.map((item) => String(item || '').trim()).filter(Boolean);
  }
  if (typeof task.requiredDocumentsText === 'string' && task.requiredDocumentsText.trim()) {
    return task.requiredDocumentsText
      .split(/\n|,|;/)
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

const collectHireTasks = (hire = {}) => {
  const allTasks = [];
  const stageTasks = hire?.tasks || {};
  Object.entries(stageTasks).forEach(([stageName, stageData]) => {
    const taskEntries = stageData?.workflow?.tasks
      ? Object.entries(stageData.workflow.tasks)
      : Object.entries(stageData || {}).filter(([key, value]) => key !== 'workflow' && value && typeof value === 'object' && value.title);

    taskEntries.forEach(([taskId, task]) => {
      allTasks.push({
        id: taskId,
        stage: stageName,
        ...(task || {})
      });
    });
  });
  return allTasks;
};

const collectHireStageTaskEntries = (hire = {}, stageName) => {
  const stageData = hire?.tasks?.[stageName];
  if (!stageData || typeof stageData !== 'object') return [];

  if (stageData.workflow?.tasks) {
    return Object.entries(stageData.workflow.tasks)
      .filter(([, task]) => task && typeof task === 'object')
      .map(([id, task]) => ({
        ...task,
        id,
        stage: stageName,
        __isWorkflowTask: true
      }));
  }

  return Object.entries(stageData)
    .filter(([key, task]) => key !== 'workflow' && task && typeof task === 'object' && task.title)
    .map(([id, task]) => ({
      ...task,
      id,
      stage: stageName,
      __isWorkflowTask: false
    }));
};

const flattenForCsv = (value) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.map((item) => flattenForCsv(item)).join(' | ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const csvEscape = (value) => {
  const raw = flattenForCsv(value);
  if (/[",\n]/.test(raw)) {
    return `"${raw.replace(/"/g, '""')}"`;
  }
  return raw;
};

const normalizeAssignmentToken = (value) => String(value || '').toLowerCase().trim();

const collectTaskAssignmentValues = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.flatMap(collectTaskAssignmentValues);
  if (typeof value === 'object') {
    const directValues = [
      value.uid,
      value.id,
      value.key,
      value.userId,
      value.userUID,
      value.userUid,
      value.email,
      value.name,
      value.displayName,
      value.assignee,
      value.assigneeEmail,
      value.assigneeName,
      value.assignedTo,
      value.assignedToEmail,
      value.assignedToName
    ];
    const mapValues = Object.entries(value).flatMap(([key, entryValue]) => {
      if (entryValue === true) return [key];
      if (entryValue && typeof entryValue === 'object') return [key, ...collectTaskAssignmentValues(entryValue)];
      return [entryValue];
    });
    return [...directValues, ...mapValues].filter(Boolean);
  }
  return [value];
};

const getTaskAssignmentValues = (task = {}) => ([
  task.assignee,
  task.assigneeEmail,
  task.assigneeName,
  task.assigneeUid,
  task.assigneeUID,
  task.assigneeId,
  task.assignedTo,
  task.assignedToEmail,
  task.assignedToName,
  task.assignedToUid,
  task.assignedToUID,
  task.assignedToId,
  task.owner,
  task.ownerEmail,
  task.ownerName,
  task.ownerUid,
  task.ownerUID,
  task.ownerId,
  task.assignees,
  task.assignedUsers,
  task.assignedUserIds,
  task.assignedToUserIds,
  task.assigneeIds
].flatMap(collectTaskAssignmentValues).map(normalizeAssignmentToken).filter(Boolean));

const Onboarding = () => {
  useUiLanguage();
  const { currentUser } = useAuth();
  const { t, i18n } = useTranslation(['onboarding', 'common']);
  const { isRTL, language } = useLanguage();
  const [sidebarCollapsed, setSidebarCollapsed] = useSidebarState();
  const [companyUID, setCompanyUID] = useState('');
  const [loading, setLoading] = useState(true);
  const teamAccess = useTeamAccessProfile(companyUID);
  const canManageOnboarding = teamAccess.isCompanyOwner || teamAccess.userPermissions.canManageOnboarding;
  const canConfirmEmployeeStarted = teamAccess.isCompanyOwner
    || teamAccess.userPermissions.canManageOnboarding
    || teamAccess.userPermissions.canManageEmployees;
  const canDeliverLifecycleEvents = teamAccess.isCompanyOwner
    || teamAccess.userPermissions.canManageEmployees
    || teamAccess.userPermissions.canManageOnboarding
    || teamAccess.userPermissions.canManageApplicants
    || teamAccess.userPermissions.canManageNationalization;

  // State for onboarding data
  const [newHires, setNewHires] = useState([]);
  const [selectedHire, setSelectedHire] = useState(null);
  const [actualStartDate, setActualStartDate] = useState('');
  const [confirmingEmployeeStarted, setConfirmingEmployeeStarted] = useState(false);
  const startDialogRef = useRef(null);
  const [nationalizationEstablishments, setNationalizationEstablishments] = useState([]);
  const [newHireEstablishmentId, setNewHireEstablishmentId] = useState('');
  const [myTasksToday, setMyTasksToday] = useState([]);
  const [showAddHireModal, setShowAddHireModal] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [activeHireDepartmentFilter, setActiveHireDepartmentFilter] = useState('all');
  const [activeTasksPanelTab, setActiveTasksPanelTab] = useState('tasks');

  // NEW: Workflow view state
  const [viewMode, setViewMode] = useState('tasks'); // 'tasks' | 'workflow' | 'categories' | 'documents'
  const [taskToOpen, setTaskToOpen] = useState(null);
  const [activeStage, setActiveStage] = useState('Before Day One');
  const [externalContacts, setExternalContacts] = useState([]);
  const [showContactsModal, setShowContactsModal] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [showAutomatedTaskModal, setShowAutomatedTaskModal] = useState(false);
  const [showHumanTaskModal, setShowHumanTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null); // Task being edited, null = creating new
  const [showFormBuilderModal, setShowFormBuilderModal] = useState(false);
  const STAGES = ['Before Day One', 'Day One', 'First Week', 'First Month', '90-Day Review'];

  // Tutorial state
  const [tutorialActive, setTutorialActive] = useState(false);
  const tutorialActiveRef = useRef(false);
  const tutorialBackupRef = useRef(null);
  const tutorialSampleHiresRef = useRef([]);
  const tutorialHireIdRef = useRef('');

  // Expanded categories state (for collapsible groups)
  const [expandedCategories, setExpandedCategories] = useState({
    'Before Day One': true,
    'Day One': true,
    'First Week': true,
    'First Month': true,
    '90-Day Review': true
  });
  const [categoryTaskMenu, setCategoryTaskMenu] = useState(null);
  const [categoryTaskMenuPosition, setCategoryTaskMenuPosition] = useState(null);

  useEffect(() => {
    if (!categoryTaskMenu) return undefined;

    const closeMenu = () => {
      setCategoryTaskMenu(null);
      setCategoryTaskMenuPosition(null);
    };

    window.addEventListener('resize', closeMenu);
    window.addEventListener('scroll', closeMenu, true);
    return () => {
      window.removeEventListener('resize', closeMenu);
      window.removeEventListener('scroll', closeMenu, true);
    };
  }, [categoryTaskMenu]);

  // Task creation modal states
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [taskCategory, setTaskCategory] = useState('');
  const [taskForm, setTaskForm] = useState({
    type: 'standard', // 'standard', 'contract', 'document', 'documentRequest'
    title: '',
    description: '',
    dueDate: '',
    requiredDocumentsText: '',
    assignees: [],             // Multi-select: array of emails (includes new hire, manager, IT, team)
    includeEmail: false,       // Enable scheduled email
    emailConfig: {
      subject: '',
      body: '',
      scheduledFor: '',        // ISO datetime for auto-send (local time)
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, // Employer's timezone
      recipients: 'newHire'    // 'newHire' | 'assignees' | 'custom'
    },
    contractTemplateId: '',
    uploadedFile: null
  });
  const [selectedHireDocuments, setSelectedHireDocuments] = useState({});
  const [selectedHireDocumentsLoading, setSelectedHireDocumentsLoading] = useState(false);
  const [exportingHireData, setExportingHireData] = useState(false);

  // Contract templates
  const [contractTemplates, setContractTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Placeholder modal for contract templates
  const [showPlaceholderModal, setShowPlaceholderModal] = useState(false);
  const [currentTaskForPlaceholders, setCurrentTaskForPlaceholders] = useState(null);
  const [placeholderValues, setPlaceholderValues] = useState({});

  // Sync selectedHire with newHires updates (Fix for Refresh Issue)
  // Sync selectedHire with newHires updates (Fix for Refresh Issue)
  useEffect(() => {
    if (selectedHire && newHires.length > 0) {
      const updatedHire = newHires.find(h => h.id === selectedHire.id);
      // Update if data changed (naive check, but effective for this context)
      // Only sync if the UPSTREAM data (updatedHire) is effectively different from local
      if (updatedHire && JSON.stringify(updatedHire) !== JSON.stringify(selectedHire)) {
        // Safe to update if the changes are coming from server, or if server caught up
        // But checking strict equality might still be tricky if local has optimistic changes.
        // However, removing selectedHire from deps prevents the immediate self-trigger.
        setSelectedHire(updatedHire);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newHires]);

  useEffect(() => {
    if (!selectedHire) {
      setActualStartDate('');
      return;
    }
    const today = localIsoDate();
    const savedDate = String(
      selectedHire.actualStartDate || selectedHire.startDate || ''
    ).slice(0, 10);
    // The planned date is only a convenient editable default. A future planned
    // date cannot be submitted as proof that employment has already started.
    setActualStartDate(savedDate && savedDate <= today ? savedDate : today);
  }, [selectedHire?.id, selectedHire?.actualStartDate, selectedHire?.startDate]);

  useEffect(() => {
    if (!companyUID) {
      setNationalizationEstablishments([]);
      setNewHireEstablishmentId('');
      return undefined;
    }
    const controller = new AbortController();
    nationalizationService.getOperationalContext(companyUID, { signal: controller.signal })
      .then((result) => {
        const establishments = Array.isArray(result?.establishments) ? result.establishments : [];
        setNationalizationEstablishments(establishments);
        setNewHireEstablishmentId((current) => (
          establishments.some(({ id }) => id === current)
            ? current
            : establishments.length === 1 ? establishments[0].id : ''
        ));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          // Hiring may continue, but the durable lifecycle event will remain
          // pending rather than guessing a legal branch.
          console.warn('Legal establishment choices could not be loaded:', error);
        }
      });
    return () => controller.abort();
  }, [companyUID]);
  const [detectedPlaceholders, setDetectedPlaceholders] = useState([]);

  // Department template management
  const [showTemplateActions, setShowTemplateActions] = useState(false);

  // Email preview/edit modal
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [emailDraft, setEmailDraft] = useState({
    to: '',
    toName: '',
    subject: '',
    message: '',
    task: null  // Store task reference for attachments
  });

  // Form state for adding new hire
  const [newHireForm, setNewHireForm] = useState({
    name: '',
    email: '',
    role: '',
    department: '',
    startDate: '',
    manager: '',
    buddyMentor: ''
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD
    || process.env.REACT_APP_BACKEND_URL
    || 'http://localhost:8000';

  const departmentOptionsList = useMemo(() => ([
    { value: 'Administration', label: t('departments.administration') },
    { value: 'Customer Support', label: t('departments.customerSupport') },
    { value: 'Engineering', label: t('departments.engineering') },
    { value: 'Product Management', label: t('departments.productManagement') },
    { value: 'Design / UX', label: t('departments.designUx') },
    { value: 'Data & Analytics', label: t('departments.dataAnalytics') },
    { value: 'Marketing', label: t('departments.marketing') },
    { value: 'Sales', label: t('departments.sales') },
    { value: 'Finance & Accounting', label: t('departments.financeAccounting') },
    { value: 'Human Resources', label: t('departments.hr') },
    { value: 'Operations', label: t('departments.operations') },
    { value: 'IT / Systems', label: t('departments.itSystems') },
    { value: 'Security / InfoSec', label: t('departments.security') },
    { value: 'Legal', label: t('departments.legal') },
    { value: 'Compliance', label: t('departments.compliance') },
    { value: 'Procurement', label: t('departments.procurement') },
    { value: 'Supply Chain', label: t('departments.supplyChain') },
    { value: 'Logistics', label: t('departments.logistics') },
    { value: 'Manufacturing', label: t('departments.manufacturing') },
    { value: 'Quality Assurance', label: t('departments.qualityAssurance') },
    { value: 'Research & Development', label: t('departments.researchDevelopment') },
    { value: 'Business Development', label: t('departments.businessDevelopment') },
    { value: 'Partnerships', label: t('departments.partnerships') },
    { value: 'Strategy', label: t('departments.strategy') },
    { value: 'Content', label: t('departments.content') },
    { value: 'Communications / PR', label: t('departments.communications') },
    { value: 'Community', label: t('departments.community') },
    { value: 'Facilities', label: t('departments.facilities') },
    { value: 'Real Estate', label: t('departments.realEstate') },
    { value: 'Construction', label: t('departments.construction') },
    { value: 'Healthcare', label: t('departments.healthcare') },
    { value: 'Education', label: t('departments.education') }
  ]), [t]);

  const departmentCanonicalMap = useMemo(() => {
    const map = {};
    departmentOptionsList.forEach(opt => {
      map[opt.value] = opt.value;
      map[opt.label] = opt.value;
    });
    return map;
  }, [departmentOptionsList]);

  const normalizeDepartment = (dept) => {
    if (!dept) return '';
    return departmentCanonicalMap[dept] || dept;
  };

  const getDepartmentLabel = (dept) => {
    if (!dept) return '';
    const canonical = normalizeDepartment(dept);
    const option = departmentOptionsList.find(opt => opt.value === canonical);
    return option ? option.label : dept;
  };

  const CATEGORY_LABELS = {
    'Before Day One': 'categories.beforeDayOne',
    'Day One': 'categories.dayOne',
    'First Week': 'categories.firstWeek',
    'First Month': 'categories.firstMonth',
    '90-Day Review': 'categories.ninetyReview',
    Completed: 'categories.completed',
    'Not Started': 'categories.notStarted'
  };

  const getCategoryLabel = (categoryKey) => {
    if (!categoryKey) return '';
    const translationKey = CATEGORY_LABELS[categoryKey];
    return translationKey ? t(translationKey) : categoryKey;
  };

  const normalizeText = (value) => String(value || '').toLowerCase().trim();
  const normalizeDepartmentKey = (value) => normalizeText(value).replace(/[^a-z0-9]+/g, ' ').trim();
  const currentTeamMember = useMemo(
    () => teamMembers.find(member => normalizeText(member?.email) === normalizeText(currentUser?.email)) || null,
    [teamMembers, currentUser?.email]
  );
  const currentUserTaskIdentifiers = useMemo(() => {
    const identifiers = [
      currentUser?.uid,
      currentUser?.email,
      currentUser?.displayName,
      currentTeamMember?.uid,
      currentTeamMember?.key,
      currentTeamMember?.id,
      currentTeamMember?.email,
      currentTeamMember?.name
    ].map(normalizeText).filter(Boolean);

    return new Set(identifiers);
  }, [currentUser?.uid, currentUser?.email, currentUser?.displayName, currentTeamMember]);
  const isTaskAssignedToCurrentUser = useCallback((task = {}) => {
    if (!currentUserTaskIdentifiers.size) return false;
    const assignmentValues = getTaskAssignmentValues(task);
    if (assignmentValues.length === 0) return false;
    return assignmentValues.some(value => currentUserTaskIdentifiers.has(value));
  }, [currentUserTaskIdentifiers]);
  const canUpdateTaskStatus = useCallback((task = {}) => (
    canManageOnboarding || isTaskAssignedToCurrentUser(task)
  ), [canManageOnboarding, isTaskAssignedToCurrentUser]);
  const showOnboardingPermissionAlert = () => {
    alert(t(
      'alerts.manageOnboardingPermissionRequired',
      'You can view onboarding and complete tasks assigned to you, but Manage onboarding is required for this action.'
    ));
  };
  const parseRequiredDocuments = (value) => (
    (value || '')
      .split(/\n|,|;/)
      .map(doc => doc.trim())
      .filter(Boolean)
  );

  const looksLikeIT = (person = {}) => {
    const haystack = [
      person.role,
      person.department,
      person.title,
      person.team,
      person.function
    ].map(normalizeText).join(' ');
    return /(it|information technology|systems|sysadmin|help\s?desk|tech|technology|devops|infrastructure)/.test(haystack);
  };

  const looksLikeHR = (person = {}) => {
    const haystack = [
      person.role,
      person.department,
      person.title,
      person.team,
      person.function
    ].map(normalizeText).join(' ');
    return /(hr|human resources|people ops|people operations|talent|recruit|owner|collaborator|team member|admin|administrator)/.test(haystack);
  };

  const looksLikeLegal = (person = {}) => {
    const haystack = [
      person.role,
      person.department,
      person.title,
      person.team,
      person.function
    ].map(normalizeText).join(' ');
    return /(legal|compliance|contracts|counsel|law)/.test(haystack);
  };

  const looksLikeFinance = (person = {}) => {
    const haystack = [
      person.role,
      person.department,
      person.title,
      person.team,
      person.function
    ].map(normalizeText).join(' ');
    return /(finance|accounting|payroll|tax|treasury|controller)/.test(haystack);
  };

  const getTaskDueDate = (startDate, relativeDays = 0) => {
    const dueDate = new Date(startDate);
    dueDate.setDate(dueDate.getDate() + (Number(relativeDays) || 0));
    return dueDate.toISOString().split('T')[0];
  };

  const getEmployerAppBaseUrl = () => {
    const configuredBase =
      process.env.REACT_APP_EMPLOYER_FRONTEND_URL ||
      process.env.REACT_APP_FRONTEND_URL_PROD ||
      process.env.REACT_APP_FRONTEND_URL;

    if (configuredBase) return configuredBase.replace(/\/$/, '');

    if (typeof window !== 'undefined' && window.location?.origin) {
      const hostname = window.location.hostname || '';
      if (hostname && hostname !== 'localhost' && hostname !== '127.0.0.1') {
        return window.location.origin.replace(/\/$/, '');
      }
      return window.location.origin.replace(/\/$/, '');
    }

    return 'https://employer.jobseeqr.co';
  };

  const buildPortalInviteLink = (hireId, token) => {
    const base = (process.env.REACT_APP_EMPLOYEE_PORTAL_URL || 'http://localhost:3001').replace(/\/$/, '');
    return `${base}/accept-invite?token=${token}&cid=${companyUID}&eid=${hireId}`;
  };

  const getCurrentAuthToken = async () => {
    if (!currentUser) return null;
    try {
      return await currentUser.getIdToken();
    } catch (error) {
      console.error('Failed to get auth token:', error);
      return null;
    }
  };

  const generateDocumentUploadPublicLink = async ({ hireId, stage, taskId, authToken }) => {
    const token = authToken || await getCurrentAuthToken();
    if (!token) return null;
    try {
      const response = await fetch(`${BACKEND_URL}/onboarding/workflow/generate-document-upload-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          companyUID,
          hireId,
          stage,
          taskId
        })
      });
      if (!response.ok) return null;
      const data = await response.json();
      if (!data?.token) return null;
      return `${getEmployerAppBaseUrl()}/onboarding-docs/${data.token}`;
    } catch (error) {
      console.error('Failed to generate document upload token:', error);
      return null;
    }
  };

  const generateExternalTaskCompletionLink = async ({ hireId, stage, taskId, assigneeEmail, authToken }) => {
    const token = authToken || await getCurrentAuthToken();
    if (!token) return null;
    try {
      const response = await fetch(`${BACKEND_URL}/onboarding/workflow/generate-external-task-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          companyUID,
          hireId,
          stage,
          taskId,
          assigneeEmail
        })
      });
      if (!response.ok) return null;
      const data = await response.json();
      if (!data?.token) return null;
      return `${getEmployerAppBaseUrl()}/onboarding-task-complete/${data.token}`;
    } catch (error) {
      console.error('Failed to generate external completion token:', error);
      return null;
    }
  };

  const pickFirstByPriority = (people = []) => {
    const seen = new Set();
    for (const person of people) {
      const email = normalizeText(person?.email);
      if (!email || seen.has(email)) continue;
      seen.add(email);
      return {
        email: person.email,
        name: person.name || person.email,
        department: person.department || person.role || '',
        source: person.source || ''
      };
    }
    return null;
  };

  const getPersonByEmail = (email) => {
    const normalized = normalizeText(email);
    if (!normalized) return null;
    const fromTeam = teamMembers.find(member => normalizeText(member.email) === normalized);
    if (fromTeam) {
      return {
        email: fromTeam.email,
        name: fromTeam.name || fromTeam.email,
        department: fromTeam.department || fromTeam.role || ''
      };
    }
    const fromExternal = externalContacts.find(contact => normalizeText(contact.email) === normalized);
    if (fromExternal) {
      return {
        email: fromExternal.email,
        name: fromExternal.name || fromExternal.email,
        department: fromExternal.department || fromExternal.role || ''
      };
    }
    return {
      email,
      name: email,
      department: ''
    };
  };

  const getPersonByIdentifier = (identifier) => {
    const raw = String(identifier || '').trim();
    if (!raw) return null;
    if (raw.includes('@')) return getPersonByEmail(raw);

    const normalized = normalizeText(raw);
    const fromTeam = teamMembers.find(member => normalizeText(member?.name) === normalized);
    if (fromTeam) {
      return {
        email: fromTeam.email,
        name: fromTeam.name || fromTeam.email,
        department: fromTeam.department || fromTeam.role || ''
      };
    }
    const fromExternal = externalContacts.find(contact => normalizeText(contact?.name) === normalized);
    if (fromExternal) {
      return {
        email: fromExternal.email,
        name: fromExternal.name || fromExternal.email,
        department: fromExternal.department || fromExternal.role || ''
      };
    }
    return null;
  };

  const getTemplateRolePeople = (templateContext, category) => {
    const cat = normalizeText(category).replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    const people = Array.isArray(templateContext?.teamRoles?.[cat])
      ? templateContext.teamRoles[cat]
      : [];
    return people
      .map(person => ({
        email: person?.email,
        name: person?.name || person?.email,
        department: person?.department || person?.role || cat.toUpperCase(),
        source: person?.source || 'template_team'
      }))
      .filter(person => person.email);
  };

  const resolveAssigneeByCategory = (category, hireContext, templateContext = null) => {
    const cat = normalizeText(category).replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    const manager = getPersonByEmail(hireContext?.manager);
    const newHire = getPersonByEmail(hireContext?.email);

    if (cat === 'new_hire' || cat === 'newhire') return newHire;
    if (cat === 'manager') return manager;

    const templateRolePick = pickFirstByPriority(getTemplateRolePeople(templateContext, cat));

    if (cat === 'it') {
      const itPick = pickFirstByPriority([
        ...externalContacts.filter(looksLikeIT),
        ...teamMembers.filter(looksLikeIT)
      ]);
      return itPick || templateRolePick || manager || newHire;
    }

    if (cat === 'hr') {
      const hrPick = pickFirstByPriority([
        ...teamMembers.filter(looksLikeHR),
        ...externalContacts.filter(looksLikeHR),
        ...teamMembers
      ]);
      return hrPick || templateRolePick || manager || newHire;
    }

    if (cat === 'legal') {
      const legalPick = pickFirstByPriority([
        ...externalContacts.filter(looksLikeLegal),
        ...teamMembers.filter(looksLikeLegal)
      ]);
      return legalPick || templateRolePick || manager || newHire;
    }

    if (cat === 'finance') {
      const financePick = pickFirstByPriority([
        ...externalContacts.filter(looksLikeFinance),
        ...teamMembers.filter(looksLikeFinance)
      ]);
      return financePick || templateRolePick || manager || newHire;
    }

    if (cat === 'other_dept' || cat === 'other_department') {
      return pickFirstByPriority(externalContacts) || templateRolePick || manager || newHire;
    }

    return pickFirstByPriority(externalContacts) || templateRolePick || manager || newHire || null;
  };

  const matchesDepartment = (templateDepartment, hireDepartment) => {
    const templateKey = normalizeDepartmentKey(templateDepartment);
    const hireKey = normalizeDepartmentKey(hireDepartment);

    if (!templateKey || templateKey === 'all' || templateKey === 'default') return true;
    if (!hireKey) return false;
    if (templateKey === hireKey) return true;
    if (templateKey.includes(hireKey) || hireKey.includes(templateKey)) return true;

    const templateAliases = new Set([templateKey]);
    const hireAliases = new Set([hireKey]);

    if (/(^| )it( |$)|information technology|systems|tech/.test(templateKey)) {
      templateAliases.add('it');
      templateAliases.add('it systems');
    }
    if (/(^| )it( |$)|information technology|systems|tech/.test(hireKey)) {
      hireAliases.add('it');
      hireAliases.add('it systems');
    }
    if (/human resources|(^| )hr( |$)/.test(templateKey)) {
      templateAliases.add('hr');
      templateAliases.add('human resources');
    }
    if (/human resources|(^| )hr( |$)/.test(hireKey)) {
      hireAliases.add('hr');
      hireAliases.add('human resources');
    }

    return [...templateAliases].some(alias => hireAliases.has(alias));
  };

  const inferAssigneeCategory = (task = {}) => {
    const combined = normalizeText(`${task.title || ''} ${task.description || ''}`);
    if (/(workstation|it setup|email account|laptop|computer|access|software|hardware)/.test(combined)) {
      return 'it';
    }
    if (/(welcome|orientation|handbook|policies|documents|preboarding|pre-boarding|training)/.test(combined)) {
      return 'hr';
    }
    if (/(manager|goals|project|review|checkin|stakeholder|1-on-1|one-on-one)/.test(combined)) {
      return 'manager';
    }
    return 'manager';
  };

  // Task templates for onboarding stages
  const taskTemplates = useMemo(() => ({
    'Before Day One': [
      { title: t('taskTemplates.beforeDayOne.sendWelcomeEmail.title'), description: t('taskTemplates.beforeDayOne.sendWelcomeEmail.description'), defaultDays: -7 },
      { title: t('taskTemplates.beforeDayOne.prepareWorkstation.title'), description: t('taskTemplates.beforeDayOne.prepareWorkstation.description'), defaultDays: -3 },
      { title: t('taskTemplates.beforeDayOne.createEmailAccount.title'), description: t('taskTemplates.beforeDayOne.createEmailAccount.description'), defaultDays: -3 },
      { title: t('taskTemplates.beforeDayOne.prepareWelcomeKit.title'), description: t('taskTemplates.beforeDayOne.prepareWelcomeKit.description'), defaultDays: -2 },
      { title: t('taskTemplates.beforeDayOne.scheduleOrientation.title'), description: t('taskTemplates.beforeDayOne.scheduleOrientation.description'), defaultDays: -2 },
      { title: t('taskTemplates.beforeDayOne.sendPreboardingDocs.title'), description: t('taskTemplates.beforeDayOne.sendPreboardingDocs.description'), defaultDays: -5, type: 'document', includeEmail: true },
      { title: t('taskTemplates.beforeDayOne.requestPreboardingDocs.title'), description: t('taskTemplates.beforeDayOne.requestPreboardingDocs.description'), defaultDays: -7, type: 'documentRequest' }
    ],
    'Day One': [
      { title: t('taskTemplates.dayOne.welcomeTour.title'), description: t('taskTemplates.dayOne.welcomeTour.description'), defaultDays: 0 },
      { title: t('taskTemplates.dayOne.itSetup.title'), description: t('taskTemplates.dayOne.itSetup.description'), defaultDays: 0 },
      { title: t('taskTemplates.dayOne.teamIntroduction.title'), description: t('taskTemplates.dayOne.teamIntroduction.description'), defaultDays: 0 },
      { title: t('taskTemplates.dayOne.reviewPolicies.title'), description: t('taskTemplates.dayOne.reviewPolicies.description'), defaultDays: 0 },
      { title: t('taskTemplates.dayOne.teamLunch.title'), description: t('taskTemplates.dayOne.teamLunch.description'), defaultDays: 0 }
    ],
    'First Week': [
      { title: t('taskTemplates.firstWeek.departmentOverview.title'), description: t('taskTemplates.firstWeek.departmentOverview.description'), defaultDays: 2 },
      { title: t('taskTemplates.firstWeek.keyStakeholders.title'), description: t('taskTemplates.firstWeek.keyStakeholders.description'), defaultDays: 3 },
      { title: t('taskTemplates.firstWeek.trainingModules.title'), description: t('taskTemplates.firstWeek.trainingModules.description'), defaultDays: 1 },
      { title: t('taskTemplates.firstWeek.reviewResponsibilities.title'), description: t('taskTemplates.firstWeek.reviewResponsibilities.description'), defaultDays: 2 },
      { title: t('taskTemplates.firstWeek.firstWeekCheckin.title'), description: t('taskTemplates.firstWeek.firstWeekCheckin.description'), defaultDays: 5 }
    ],
    'First Month': [
      { title: t('taskTemplates.firstMonth.thirtyDayGoals.title'), description: t('taskTemplates.firstMonth.thirtyDayGoals.description'), defaultDays: 7 },
      { title: t('taskTemplates.firstMonth.firstProject.title'), description: t('taskTemplates.firstMonth.firstProject.description'), defaultDays: 10 },
      { title: t('taskTemplates.firstMonth.recurringOneOnOnes.title'), description: t('taskTemplates.firstMonth.recurringOneOnOnes.description'), defaultDays: 7 },
      { title: t('taskTemplates.firstMonth.thirtyDayFeedback.title'), description: t('taskTemplates.firstMonth.thirtyDayFeedback.description'), defaultDays: 30 }
    ],
    '90-Day Review': [
      { title: t('taskTemplates.ninetyDayReview.reviewPrep.title'), description: t('taskTemplates.ninetyDayReview.reviewPrep.description'), defaultDays: 85 },
      { title: t('taskTemplates.ninetyDayReview.performanceReview.title'), description: t('taskTemplates.ninetyDayReview.performanceReview.description'), defaultDays: 90 },
      { title: t('taskTemplates.ninetyDayReview.quarterlyGoals.title'), description: t('taskTemplates.ninetyDayReview.quarterlyGoals.description'), defaultDays: 90 }
    ]
  }), [t]);

  const selectHireForView = (hire, options = {}) => {
    if (!hire) {
      setSelectedHire(null);
      return null;
    }

    const hireClone = cloneData(hire);
    if (hireClone.department) {
      hireClone.department = normalizeDepartment(hireClone.department);
    }
    setSelectedHire(hireClone);
    setTaskToOpen(null);
    setViewMode('tasks');
    setActiveStage('Before Day One');

    if (options.trackTutorialHire) {
      tutorialHireIdRef.current = hire.id;
    }

    return hireClone;
  };

  const confirmEmployeeStarted = async () => {
    if (!companyUID || !selectedHire?.id || !canConfirmEmployeeStarted) return;
    const today = localIsoDate();
    const normalizedDate = String(actualStartDate || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(normalizedDate) || localIsoDate(`${normalizedDate}T00:00:00`) !== normalizedDate) {
      alert(t('alerts.invalidActualStartDate', 'Enter a valid actual start date.'));
      return;
    }
    if (normalizedDate > today) {
      alert(t('alerts.futureActualStartDate', 'The actual start date cannot be in the future.'));
      return;
    }

    setConfirmingEmployeeStarted(true);
    try {
      const employeePath = `companies/${companyUID}/employees/${selectedHire.id}`;
      const onboardingPath = `companies/${companyUID}/onboarding/${selectedHire.id}`;
      const confirmedAt = Date.now();
      const updates = {
        [`${onboardingPath}/status`]: 'active',
        [`${onboardingPath}/actualStartDate`]: normalizedDate,
        [`${onboardingPath}/actualStartConfirmedAt`]: confirmedAt,
        [`${onboardingPath}/actualStartConfirmedBy`]: currentUser?.uid || '',
        [`${employeePath}/status`]: 'active',
        [`${employeePath}/lifecycle/status`]: 'active',
        [`${employeePath}/lifecycle/startDate`]: normalizedDate,
        // Keep the explicit confirmation evidence beside the employee lifecycle.
        // Legacy records sometimes contain only status="active"; these fields let
        // every later workflow distinguish a confirmed start from that old state.
        [`${employeePath}/lifecycle/actualStartConfirmedAt`]: confirmedAt,
        [`${employeePath}/lifecycle/actualStartConfirmedBy`]: currentUser?.uid || '',
        [`${employeePath}/lifecycle/lastActivityAt`]: confirmedAt,
        [`${employeePath}/profile/startDate`]: normalizedDate
      };
      // This explicit confirmation is legally different from completing an
      // onboarding checklist. Its outbox marker commits in the same write, so a
      // network loss cannot leave the workforce calculation unaware of the start.
      const lifecycleEvent = addNationalizationLifecycleEventToUpdates(updates, {
        companyUID,
        employeeId: selectedHire.id,
        source: 'employee_start_confirmed',
        changeKinds: ['lifecycle']
      });
      await update(ref(database), updates);

      let lifecycleSync = null;
      try {
        lifecycleSync = await nationalizationService.syncEmployeeLifecycle(companyUID, selectedHire.id, {
          eventId: lifecycleEvent.eventId,
          source: 'employee_start_confirmed',
          changeKinds: ['lifecycle']
        });
      } catch (syncError) {
        console.warn('Nationalization actual-start sync is pending:', syncError);
      }
      setSelectedHire((current) => current ? {
        ...current,
        status: 'active',
        actualStartDate: normalizedDate,
        actualStartConfirmedAt: confirmedAt
      } : current);
      announceNationalizationWorkforceChange({
        companyUID,
        source: 'employee_start_confirmed',
        recalculated: lifecycleSync?.recalculated === true,
        syncPending: !lifecycleSync
      });
      alert(t('alerts.employeeStartConfirmed', 'Employee start confirmed.'));
    } catch (error) {
      console.error('Failed to confirm employee start:', error);
      alert(t('alerts.employeeStartConfirmFailed', 'Could not confirm the employee start. Please try again.'));
    } finally {
      setConfirmingEmployeeStarted(false);
    }
  };

  const triggerClick = (selectorOrElement) => {
    const element = typeof selectorOrElement === 'string'
      ? document.querySelector(selectorOrElement)
      : selectorOrElement;

    if (!element || typeof element.click !== 'function') {
      return false;
    }

    element.click();
    return true;
  };

  useEffect(() => {
    tutorialActiveRef.current = tutorialActive;
  }, [tutorialActive]);

  // Authentication and company setup
  useEffect(() => {
    if (currentUser) {
      authenticateAndSetupCompany();
    } else {
      setLoading(false);
    }
  }, [currentUser]);

  // Load data when companyUID is set
  useEffect(() => {
    if (companyUID) {
      loadNewHires();
      loadTeamMembers();
      loadExternalContacts();
    }
  }, [companyUID]);

  useEffect(() => {
    if (!companyUID || teamAccess.loading || !canDeliverLifecycleEvents) return undefined;
    return startNationalizationLifecycleReplay(companyUID);
  }, [canDeliverLifecycleEvents, companyUID, teamAccess.loading]);

  // Calculate my tasks for today
	  useEffect(() => {
	    if (tutorialActive) {
	      return;
	    }
	    if (currentUser && newHires.length > 0) {
	      const todayTime = getTodayLocalDate().getTime();

	      const allTasks = [];
	      newHires.forEach(hire => {
	        if (hire.tasks) {
	          Object.entries(hire.tasks).forEach(([category, categoryData]) => {
		            // Helper to process a single task
		            const processTask = (taskId, task) => {
		              if (!task || typeof task !== 'object') return;
		              const dueDate = getTaskLocalDate(
		                task.dueDate || task.due_date || task.deadline || task.date || task.scheduledFor || task.scheduled_for
		              );
		              if (!dueDate || dueDate.getTime() !== todayTime) return;

	              const isAssigned = isTaskAssignedToCurrentUser(task);

              if (isAssigned) {
                allTasks.push({
                  ...task,
                  hireId: hire.id,
                  hireName: hire.name,
                  category,
                  taskId
                });
              }
            };

            // 1. Check for workflow structure: categoryData.workflow.tasks
            if (categoryData?.workflow?.tasks) {
              Object.entries(categoryData.workflow.tasks).forEach(([taskId, task]) => {
                processTask(taskId, task);
              });
            }

            // 2. Check legacy structure: direct task entries under categoryData (excluding 'workflow' key)
            Object.entries(categoryData).forEach(([key, value]) => {
              if (key !== 'workflow' && value && typeof value === 'object' && value.title) {
                processTask(key, value);
              }
            });
          });
        }
      });
      setMyTasksToday(allTasks);
    } else {
      setMyTasksToday([]);
    }
	  }, [newHires, currentUser, teamMembers, tutorialActive, isTaskAssignedToCurrentUser]);

  useEffect(() => {
    if (!companyUID || !selectedHire?.id) {
      setSelectedHireDocuments({});
      setSelectedHireDocumentsLoading(false);
      return undefined;
    }

    setSelectedHireDocumentsLoading(true);
    const docsRef = ref(database, `companies/${companyUID}/employees/${selectedHire.id}/documents`);
    const unsubscribe = onValue(
      docsRef,
      (snapshot) => {
        setSelectedHireDocuments(snapshot.exists() ? snapshot.val() : {});
        setSelectedHireDocumentsLoading(false);
      },
      (error) => {
        console.error('Error loading hire documents:', error);
        setSelectedHireDocuments({});
        setSelectedHireDocumentsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [companyUID, selectedHire?.id]);

  const handleExportHireData = async () => {
    if (!companyUID || !selectedHire?.id || exportingHireData) return;
    if (!canManageOnboarding) {
      showOnboardingPermissionAlert();
      return;
    }

    setExportingHireData(true);
    try {
      const hireId = selectedHire.id;
      const [onboardingSnap, employeeSnap, docsSnap] = await Promise.all([
        get(ref(database, `companies/${companyUID}/onboarding/${hireId}`)),
        get(ref(database, `companies/${companyUID}/employees/${hireId}`)),
        get(ref(database, `companies/${companyUID}/employees/${hireId}/documents`))
      ]);

      const onboardingData = onboardingSnap.exists() ? onboardingSnap.val() : (selectedHire || {});
      const employeeData = employeeSnap.exists() ? employeeSnap.val() : {};
      const documentsData = docsSnap.exists() ? docsSnap.val() : (selectedHireDocuments || {});
      const allTasks = collectHireTasks(onboardingData);

      const formResponseColumns = {};
      allTasks.forEach((task) => {
        const response = task?.formResponse;
        if (!response || typeof response !== 'object') return;
        Object.entries(response).forEach(([fieldKey, fieldValue]) => {
          const taskTitle = String(task?.title || 'form').trim().replace(/\s+/g, ' ');
          const safeTaskTitle = taskTitle || 'form';
          const colKey = `form_${safeTaskTitle}_${fieldKey}`
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '');
          formResponseColumns[colKey] = flattenForCsv(fieldValue);
        });
      });

      const baseRow = {
        new_hire_id: hireId,
        full_name: onboardingData?.name || selectedHire?.name || '',
        personal_email: onboardingData?.email || selectedHire?.email || '',
        work_email: employeeData?.workEmail || employeeData?.email || '',
        phone_number: onboardingData?.phone || employeeData?.phone || '',
        job_position: onboardingData?.role || employeeData?.role || '',
        department: onboardingData?.department || employeeData?.department || '',
        start_date: onboardingData?.startDate || employeeData?.startDate || '',
        assigned_manager: onboardingData?.manager || employeeData?.managerName || employeeData?.manager || '',
        buddy_mentor: onboardingData?.buddyMentor || '',
        employment_type: employeeData?.employmentType || '',
        onboarding_status: onboardingData?.status || 'onboarding'
      };

      const csvColumns = [...Object.keys(baseRow), ...Object.keys(formResponseColumns).sort()];
      const csvRow = {
        ...baseRow,
        ...formResponseColumns
      };
      const csvContent = [
        csvColumns.join(','),
        csvColumns.map((column) => csvEscape(csvRow[column] || '')).join(',')
      ].join('\n');

      const safeName = String(baseRow.full_name || 'new_hire')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '') || 'new_hire';
      const dateStamp = new Date().toISOString().slice(0, 10);
      const zip = new JSZip();
      zip.file(`${safeName}_hris_export_${dateStamp}.csv`, csvContent);

      const rawDocumentEntries = documentsData?.files && typeof documentsData.files === 'object'
        ? Object.entries(documentsData.files).map(([id, doc]) => ({ id, ...(doc || {}) }))
        : Object.entries(documentsData || {}).map(([id, doc]) => ({ id, ...(doc || {}) }));
      const documentEntries = rawDocumentEntries.filter((doc) => doc && typeof doc === 'object');
      if (!currentUser) throw new Error(t('hrisExport.errors.signInRequired', 'Sign in before exporting employee documents.'));
      const documentToken = await currentUser.getIdToken();
      const documentHeaders = { Authorization: `Bearer ${documentToken}` };

      const fetchSignedUrl = async (documentId) => {
        const response = await fetch(`${BACKEND_URL}/employee/document-signed-url?` + new URLSearchParams({
          companyUID,
          employeeId: hireId,
          documentId,
          expirationMinutes: '15'
        }), { headers: documentHeaders });
        if (!response.ok) return null;
        const payload = await response.json();
        return payload?.signedUrl || null;
      };

      const fetchDecryptedDocumentBlob = async (documentId) => {
        const response = await fetch(`${BACKEND_URL}/employee/download-document?` + new URLSearchParams({
          companyUID,
          employeeId: hireId,
          documentId
        }), { headers: documentHeaders });
        if (!response.ok) {
          throw new Error(`Failed secure download for document ${documentId}`);
        }
        return response.blob();
      };

      const sanitizeDocFileName = (name, fallbackId) => {
        const base = String(name || `document_${fallbackId || Date.now()}`).trim();
        return base.replace(/[\\/:*?"<>|]/g, '_');
      };

      const failedDocuments = [];
      for (const doc of documentEntries) {
        try {
          let blob = null;
          if (doc.encrypted) {
            blob = await fetchDecryptedDocumentBlob(doc.id);
          } else {
            const signedUrl = await fetchSignedUrl(doc.id);
            const sourceUrl = signedUrl || doc.url;
            if (!sourceUrl) throw new Error('Missing document URL');
            const fileResp = await fetch(sourceUrl);
            if (!fileResp.ok) throw new Error(`Could not fetch ${sourceUrl}`);
            blob = await fileResp.blob();
          }
          const fileName = sanitizeDocFileName(doc.name, doc.id);
          zip.file(`documents/${fileName}`, blob);
        } catch (docError) {
          console.error('Failed to export document:', doc?.id, docError);
          failedDocuments.push(doc?.name || doc?.id || 'unknown_document');
        }
      }

      if (failedDocuments.length > 0) {
        zip.file('documents/_failed_downloads.txt', failedDocuments.join('\n'));
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const downloadUrl = URL.createObjectURL(zipBlob);
      const anchor = document.createElement('a');
      anchor.href = downloadUrl;
      anchor.download = `${safeName}_new_hire_export_${dateStamp}.zip`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Failed to export new hire data:', error);
      alert(uiText('Failed to export new hire data. Please try again.'));
    } finally {
      setExportingHireData(false);
    }
  };

  const authenticateAndSetupCompany = async () => {
    try {
      setLoading(true);
      const companySnapshot = await get(ref(database, `companies/${currentUser.uid}`));

      if (companySnapshot.exists()) {
        setCompanyUID(currentUser.uid);
      } else {
        const collabSnapshot = await get(ref(database, `collaborators/${currentUser.uid}`));
        if (collabSnapshot.exists()) {
          const collaboratorData = collabSnapshot.val();
          setCompanyUID(collaboratorData.companyId);
        }
      }
    } catch (error) {
      console.error('Error during authentication:', error);
      setLoading(false);
    }
  };

  const loadNewHires = async () => {
    try {
      const hiresRef = ref(database, `companies/${companyUID}/onboarding`);
      onValue(hiresRef, (snapshot) => {
        if (tutorialActiveRef.current) {
          return;
        }
        if (snapshot.exists()) {
          const data = snapshot.val();
          const hiresArray = Object.keys(data).map(key => {
            const hireData = { id: key, ...data[key] };
            if (hireData.department) {
              hireData.department = normalizeDepartment(hireData.department);
            }
            return hireData;
          });
          // Sort by start date, newest first
          hiresArray.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
          setNewHires(hiresArray);
        } else {
          setNewHires([]);
        }
        setLoading(false);
      });
    } catch (error) {
      console.error('Error loading new hires:', error);
      setLoading(false);
    }
  };

  const loadTeamMembers = async () => {
    try {
      const members = [];
      const seenEmails = new Set();

      // Load team access profiles for enrichment
      const profilesSnap = await get(ref(database, `companies/${companyUID}/teamAccessProfiles`));
      const profiles = profilesSnap.exists() ? profilesSnap.val() || {} : {};

      const getOverlay = (uid, email) => {
        if (uid && profiles[uid]) return profiles[uid];
        if (email) {
          const key = email.trim().toLowerCase().replace(/[.#$/[\]]/g, '_');
          if (profiles[key]) return profiles[key];
        }
        return {};
      };

      // Company owner
      const companySnap = await get(ref(database, `companies/${companyUID}`));
      if (companySnap.exists()) {
        const cd = companySnap.val();
        const ownerEmail = (cd.contactEmail || cd.email || '').toLowerCase();
        if (ownerEmail && !seenEmails.has(ownerEmail)) {
          seenEmails.add(ownerEmail);
          members.push({
            uid: companyUID,
            key: companyUID,
            email: ownerEmail,
            name: cd.ownerName || cd.contactName || cd.companyName || ownerEmail,
            role: 'Owner',
            department: cd.department || 'HR'
          });
        }
      }

      // Current user as fallback owner entry
      if (currentUser?.email) {
        const cuEmail = currentUser.email.toLowerCase();
        if (!seenEmails.has(cuEmail)) {
          seenEmails.add(cuEmail);
          members.push({
            uid: currentUser.uid,
            key: currentUser.uid,
            email: currentUser.email,
            name: currentUser.displayName || currentUser.email,
            role: 'Owner',
            department: 'HR'
          });
        }
      }

      // Collaborators — query both companyId (new) and company_uid (legacy)
      const [newSnap, legacySnap] = await Promise.all([
        get(dbQuery(ref(database, 'collaborators'), orderByChild('companyId'), equalTo(companyUID))),
        get(dbQuery(ref(database, 'collaborators'), orderByChild('company_uid'), equalTo(companyUID))),
      ]);

      const addCollab = (child) => {
        const data = child.val() || {};
        const overlay = getOverlay(child.key, data.email);
        const email = (overlay.email || data.email || '').toLowerCase();
        if (!email || seenEmails.has(email)) return;
        seenEmails.add(email);
        members.push({
          uid: child.key,
          key: child.key,
          email,
          name: overlay.name || data.name || email,
          role: overlay.position || data.position || data.role || 'Team Member',
          department: data.department || overlay.department || 'HR'
        });
      };

      if (newSnap.exists()) newSnap.forEach(addCollab);
      if (legacySnap.exists()) legacySnap.forEach(addCollab);

      // Profile-only entries (invited but not yet accepted, etc.)
      Object.entries(profiles).forEach(([profileKey, pv]) => {
        const email = (pv?.email || '').toLowerCase();
        if (!email || seenEmails.has(email)) return;
        seenEmails.add(email);
        members.push({
          uid: pv.uid || profileKey,
          key: profileKey,
          email,
          name: pv.name || email,
          role: pv.position || 'Team Member',
          department: pv.department || 'HR'
        });
      });

      setTeamMembers(members);
    } catch (error) {
      console.error('Error loading team members:', error);
    }
  };

  // NEW: Load external contacts for task assignment
  const loadExternalContacts = async () => {
    try {
      const contactsRef = ref(database, `companies/${companyUID}/external_contacts`);
      const snapshot = await get(contactsRef);
      if (snapshot.exists()) {
        const data = snapshot.val();
        const contacts = Object.entries(data).map(([id, contact]) => ({ id, ...contact }));
        setExternalContacts(contacts);
        console.log('✅ Loaded external contacts:', contacts);
      }
    } catch (error) {
      console.error('Error loading external contacts:', error);
    }
  };

  // NEW: Add/update external contact
  const handleSaveExternalContact = async (contact) => {
    if (!canManageOnboarding) {
      showOnboardingPermissionAlert();
      return null;
    }
    try {
      const email = String(contact?.email || '').trim().toLowerCase();
      const name = String(contact?.name || '').trim();
      if (!name || !email) return null;

      const existingContact = (
        (contact.id ? externalContacts.find(item => item.id === contact.id) : null)
        || externalContacts.find(item => normalizeText(item.email) === normalizeText(email))
      );
      const contactId = existingContact?.id || contact.id || push(ref(database, `companies/${companyUID}/external_contacts`)).key;
      const payload = {
        ...(existingContact || {}),
        ...contact,
        id: contactId,
        name,
        email,
        department: contact.department || existingContact?.department || '',
        source: contact.source || existingContact?.source || 'external_contact',
        roleCategory: contact.roleCategory || existingContact?.roleCategory || '',
        addedAt: existingContact?.addedAt || contact.addedAt || Date.now(),
        addedBy: existingContact?.addedBy || contact.addedBy || currentUser?.email || '',
        updatedAt: Date.now(),
        updatedBy: currentUser?.email || ''
      };

      await set(ref(database, `companies/${companyUID}/external_contacts/${contactId}`), payload);
      setExternalContacts(prev => {
        const next = prev.filter(item => item.id !== contactId && normalizeText(item.email) !== normalizeText(email));
        return [...next, payload].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
      });
      return payload;
    } catch (error) {
      console.error('Error saving external contact:', error);
      alert(t('alerts.errorAddingContact'));
      return null;
    }
  };

  const handleAddExternalContact = (contact) => {
    handleSaveExternalContact(contact);
  };

  // NEW: Remove external contact
  const handleRemoveExternalContact = async (contactId) => {
    if (!canManageOnboarding) {
      showOnboardingPermissionAlert();
      return;
    }
    try {
      const contactRef = ref(database, `companies/${companyUID}/external_contacts/${contactId}`);
      await remove(contactRef);
      setExternalContacts(prev => prev.filter(c => c.id !== contactId));
    } catch (error) {
      console.error('Error removing external contact:', error);
    }
  };

  // NEW: Handle workflow task actions
  const handleWorkflowTaskAction = async (action, task) => {
    switch (action) {
      case 'complete':
        if (!canUpdateTaskStatus(task, 'completed')) {
          showOnboardingPermissionAlert();
          return;
        }
        await updateTaskStatus(selectedHire.id, activeStage, task.id, 'completed');
        break;
      case 'uncomplete':
        if (!canUpdateTaskStatus(task, 'not_started')) {
          showOnboardingPermissionAlert();
          return;
        }
        await updateTaskStatus(selectedHire.id, activeStage, task.id, 'not_started');
        break;
      case 'upload_docs':
        if (!canManageOnboarding) {
          showOnboardingPermissionAlert();
          return;
        }
        if (task.documentUploadLink) {
          window.open(task.documentUploadLink, '_blank', 'noopener,noreferrer');
        } else {
          alert(uiText('No document upload link is configured for this task yet.'));
        }
        break;
      case 'edit_email':
        if (!canManageOnboarding) {
          showOnboardingPermissionAlert();
          return;
        }
        // Open email preview with task's email draft
        if (selectedHire?.email) {
          const emailData = task.emailDraft || task.scheduledEmail || {};
          openEmailPreview(
            selectedHire.email,
            selectedHire.name,
            emailData.subject || `Task: ${task.title}`,
            emailData.body || task.description || '',
            task
          );
        }
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  // NEW: Handle adding task from workflow view
  const handleAddTaskFromWorkflow = (stage) => {
    if (!canManageOnboarding) {
      showOnboardingPermissionAlert();
      return;
    }
    setActiveStage(stage);
    // Show a choice modal for automated vs manual task.
    // For now, default to the manual task modal.
    setShowHumanTaskModal(true);
  };

  // NEW: Handle saving automated task chain
  const handleSaveAutomatedTasks = async (tasks, stage) => {
    if (!canManageOnboarding) {
      showOnboardingPermissionAlert();
      return;
    }
    try {
      const workflowRef = ref(database, `companies/${companyUID}/onboarding/${selectedHire.id}/tasks/${stage}/workflow/tasks`);
      const authToken = await getCurrentAuthToken();
      const uploadTaskAttachments = async (attachments, taskId) => {
        if (!Array.isArray(attachments) || attachments.length === 0) return [];
        const uploaded = [];
        for (const att of attachments) {
          if (att?.url && att?.name) {
            uploaded.push({ name: att.name, url: att.url, type: att.type || '' });
            continue;
          }
          const fileObj = att?.file;
          if (!fileObj) continue;
          const safeName = String(fileObj.name || att?.name || 'attachment').replace(/\s+/g, '_');
          const filePath = `companies/${companyUID}/onboarding_automation_attachments/${selectedHire.id}/${stage}/${taskId}/${Date.now()}_${safeName}`;
          const fileRef = storageRef(storage, filePath);
          await uploadBytes(fileRef, fileObj);
          const fileURL = await getDownloadURL(fileRef);
	          uploaded.push({
	            name: att?.name || fileObj.name || 'Attachment',
	            url: fileURL,
	            type: fileObj.type || att?.type || ''
	          });
	        }
	        return uploaded;
	      };

	      const applyAutomationAssignee = (task) => {
	        const automationType = task?.automationType || 'scheduled_email';
	        if (NEW_HIRE_AUTOMATION_TYPES.has(automationType)) {
	          return {
	            ...task,
	            assigneeCategory: 'new_hire',
	            assignee: selectedHire.email || '',
	            assigneeEmail: selectedHire.email || '',
	            assigneeName: selectedHire.name || t('workflow.newHire', 'New Hire'),
	            assigneeDepartment: t('workflow.newHire', 'New Hire'),
	            assigneeSource: 'new_hire'
	          };
	        }
	        return {
	          ...task,
	          assigneeCategory: 'automations',
	          assignee: 'System',
	          assigneeEmail: '',
	          assigneeName: t('workflow.systemAuto', 'System (Auto)'),
	          assigneeDepartment: t('templatesEditor.groupShort.automations', 'Automations'),
	          assigneeSource: 'system'
	        };
	      };

      if (editingTask && editingTask.id) {
        const task = tasks[0];
        if (!task) {
          alert(t('alerts.errorCreatingTask'));
          return;
        }

        const taskLocation = selectedHire.tasks?.[stage]?.workflow?.tasks?.[editingTask.id] ? `workflow/tasks/${editingTask.id}` : editingTask.id;
        const taskRef = ref(database, `companies/${companyUID}/onboarding/${selectedHire.id}/tasks/${stage}/${taskLocation}`);
	        let updatedTask = {
	          ...editingTask,
	          ...task,
	          id: editingTask.id,
	          type: 'automated',
	          status: editingTask.status || 'not_started',
	          updatedAt: Date.now()
	        };
	        updatedTask = applyAutomationAssignee(updatedTask);
        if (updatedTask.automationType === 'document_send') {
          updatedTask.attachments = await uploadTaskAttachments(updatedTask.attachments, editingTask.id);
        }
        await set(taskRef, updatedTask);
        if (authToken && updatedTask.automationType) {
          await fetch(`${BACKEND_URL}/onboarding/workflow/schedule-automation`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authToken}`
            },
            body: JSON.stringify({
              companyUID,
              hireId: selectedHire.id,
              taskId: editingTask.id,
              automationType: updatedTask.automationType,
              executeAt: updatedTask.executionMode === 'immediate' ? null : (updatedTask.dueDate || null),
              payload: {
	                stage,
	                title: updatedTask.title,
	                emailDraft: updatedTask.emailDraft,
	                emailConfig: updatedTask.emailConfig,
	                executionMode: updatedTask.executionMode,
	                formConfig: updatedTask.formConfig,
                meetingConfig: updatedTask.meetingConfig,
                requiredDocuments: updatedTask.requiredDocuments || [],
                documentUploadLink: updatedTask.documentUploadLink || null,
                attachments: updatedTask.attachments || [],
                firstName: selectedHire.name.split(' ')[0],
              }
            })
          });
        }
        setShowAutomatedTaskModal(false);
        setEditingTask(null);
        return;
      }

      // Iterate and save each task individually
      for (const task of tasks) {
        const newTaskRef = push(workflowRef);
        const taskId = newTaskRef.key;

	        let finalTask = {
	          ...task,
	          id: taskId,
	          type: 'automated',
	          status: 'not_started',
	          createdAt: Date.now()
	        };
	        finalTask = applyAutomationAssignee(finalTask);
        if (finalTask.automationType === 'document_send') {
          finalTask.attachments = await uploadTaskAttachments(finalTask.attachments, taskId);
        }

        // Generate Link for Form Collection using taskId
        if (task.automationType === 'form_collect') {
          // Token: companyId:hireId:stage:taskId
          const token = btoa(`${companyUID}:${selectedHire.id}:${stage}:${taskId}`);
          const link = `${getEmployerAppBaseUrl()}/form-collect/${token}`;

          if (finalTask.emailDraft && finalTask.emailDraft.body) {
            finalTask.emailDraft = {
              ...finalTask.emailDraft,
              body: finalTask.emailDraft.body.replace('{{form_link}}', link)
            };
          }
          finalTask.formLink = link;
        }

        if (task.automationType === 'document_request') {
          finalTask.portalUploadRequired = true;
          if (!Array.isArray(finalTask.requiredDocuments)) {
            finalTask.requiredDocuments = [];
          }
        }

        // Save to Firebase
        await set(newTaskRef, finalTask);

        if (task.automationType === 'document_request') {
          const uploadLink = await generateDocumentUploadPublicLink({
            hireId: selectedHire.id,
            stage,
            taskId,
            authToken
          });
          if (uploadLink) {
            finalTask.documentUploadLink = uploadLink;
            const taskUpdates = {
              documentUploadLink: uploadLink,
              portalUploadRequired: true
            };
            if (finalTask.emailDraft?.body) {
              finalTask.emailDraft = {
                ...finalTask.emailDraft,
                body: finalTask.emailDraft.body
                  .replace('{{portal_link}}', uploadLink)
                  .replace('{{document_upload_link}}', uploadLink)
              };
              taskUpdates.emailDraft = finalTask.emailDraft;
            }
            await update(ref(database, `companies/${companyUID}/onboarding/${selectedHire.id}/tasks/${stage}/workflow/tasks/${taskId}`), taskUpdates);
          }
        }

	        // TRIGGER CLOUD TASK (Backend Call)
        // If it is an immediate automation or a scheduled one, we notify backend to create Cloud Task
        try {
          if (authToken) {
            await fetch(`${BACKEND_URL}/onboarding/workflow/schedule-automation`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`
              },
              body: JSON.stringify({
                companyUID,
                hireId: selectedHire.id,
                taskId,
                automationType: task.automationType,
                executeAt: task.executionMode === 'immediate' ? null : (task.dueDate || null),
                payload: {
	                  stage,
	                  title: finalTask.title,
	                  emailDraft: finalTask.emailDraft,
	                  emailConfig: finalTask.emailConfig,
	                  executionMode: finalTask.executionMode,
	                  formConfig: finalTask.formConfig,
                  meetingConfig: finalTask.meetingConfig,
                  requiredDocuments: finalTask.requiredDocuments || [],
                  documentUploadLink: finalTask.documentUploadLink || null,
                  attachments: finalTask.attachments || [],
                  firstName: selectedHire.name.split(' ')[0],
                  // Add other necessary payload fields used by service
                }
              })
            });
          }
        } catch (backendError) {
          console.error('Failed to schedule Cloud Task:', backendError);
          // Don't block UI, but log error. Task is saved in Firebase anyway.
        }

      }

      setShowAutomatedTaskModal(false);
      setEditingTask(null);
      // Real-time listener (onValue) will auto-update newHires
    } catch (error) {
      console.error('Error creating automated tasks:', error);
      alert(t('alerts.errorCreatingTask'));
    }
  };

  // NEW: Handle saving manual task
  const handleSaveHumanTask = async (taskData, stage) => {
    if (!canManageOnboarding) {
      showOnboardingPermissionAlert();
      return;
    }
    try {
      const authToken = await getCurrentAuthToken();
      if (editingTask && editingTask.id) {
        // Update existing task
        const taskLocation = selectedHire.tasks?.[stage]?.workflow?.tasks?.[editingTask.id] ? `workflow/tasks/${editingTask.id}` : editingTask.id;
        const taskRef = ref(database, `companies/${companyUID}/onboarding/${selectedHire.id}/tasks/${stage}/${taskLocation}`);
        const updatedTask = {
          ...editingTask,
          ...taskData,
          id: editingTask.id,
          updatedAt: Date.now()
        };
        await set(taskRef, {
          ...updatedTask
        });
        if (updatedTask.assignee && normalizeText(updatedTask.assignee) !== normalizeText(editingTask.assignee)) {
          await notifyTaskAssignee({
            assigneeEmail: updatedTask.assignee,
            task: updatedTask,
            hire: selectedHire,
            stage,
            taskId: updatedTask.id,
            authToken
          });
        }
      } else {
        // Create new task
        const taskRef = ref(database, `companies/${companyUID}/onboarding/${selectedHire.id}/tasks/${stage}/workflow/tasks`);
        const newTaskRef = push(taskRef);
        const taskId = newTaskRef.key;
        const createdTask = {
          ...taskData,
          id: taskId,
          createdAt: Date.now()
        };

        await set(newTaskRef, {
          ...createdTask
        });
        if (createdTask.assignee) {
          await notifyTaskAssignee({
            assigneeEmail: createdTask.assignee,
            task: createdTask,
            hire: selectedHire,
            stage,
            taskId,
            authToken
          });
        }
      }

      setShowHumanTaskModal(false);
      setEditingTask(null); // Clear editing state
      // Real-time listener (onValue) will auto-update newHires
    } catch (error) {
      console.error('Error creating/updating manual task:', error);
      alert(t('alerts.errorCreatingTask'));
    }
  };

  // NEW: Handle editing task
  const handleEditTask = (task) => {
    if (!canManageOnboarding) {
      showOnboardingPermissionAlert();
      return;
    }
    setEditingTask(task); // Set task for pre-filling
    // If automated task, open automated modal; otherwise manual task modal.
    if (task.type === 'automated' || task.automationType) {
      setShowAutomatedTaskModal(true);
    } else {
      setShowHumanTaskModal(true);
    }
  };

  // NEW: Handle deleting task from workflow
  const handleDeleteWorkflowTask = async (task) => {
    if (!canManageOnboarding) {
      showOnboardingPermissionAlert();
      return;
    }
    if (!window.confirm(t('confirms.deleteTask'))) return;

    try {
      const taskRef = ref(database, `companies/${companyUID}/onboarding/${selectedHire.id}/tasks/${activeStage}/workflow/tasks/${task.id}`);
      await remove(taskRef);
      // Real-time listener (onValue) will auto-update newHires
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const forEachHireTask = (hire, callback) => {
    if (!hire?.tasks) return;
    Object.entries(hire.tasks).forEach(([categoryName, categoryData]) => {
      if (!categoryData || typeof categoryData !== 'object') return;

      const workflowTasks = categoryData?.workflow?.tasks || {};
      Object.entries(workflowTasks).forEach(([taskId, task]) => {
        if (task && typeof task === 'object') {
          callback(task, categoryName, taskId);
        }
      });

      Object.entries(categoryData).forEach(([taskId, task]) => {
        if (taskId === 'workflow') return;
        if (!task || typeof task !== 'object' || !task.title) return;
        callback(task, categoryName, taskId);
      });
    });
  };


  const calculateProgress = (hire) => {
    if (!hire.tasks) return 0;

    let totalTasks = 0;
    let completedTasks = 0;

    forEachHireTask(hire, (task) => {
      totalTasks++;
      if (task.status === 'completed') completedTasks++;
    });

    return totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  };

  const getCurrentStepKey = (hire) => {
    if (!hire.tasks) return 'Not Started';

    const categories = ['Before Day One', 'Day One', 'First Week', 'First Month', '90-Day Review'];
    let totalTasks = 0;

    // Find the first category with incomplete tasks
    for (const category of categories) {
      const tasksInCategory = [];
      forEachHireTask(hire, (task, categoryName) => {
        if (categoryName === category) {
          tasksInCategory.push(task);
          totalTasks++;
        }
      });
      if (tasksInCategory.some(task => task.status !== 'completed')) return category;
    }

    if (totalTasks === 0) return 'Not Started';
    return 'Completed';
  };

  const getTaskLocalDate = (dueDate) => {
    if (!dueDate) return null;
    if (dueDate instanceof Date) {
      const date = new Date(dueDate);
      date.setHours(0, 0, 0, 0);
      return Number.isNaN(date.getTime()) ? null : date;
    }
    if (typeof dueDate === 'number') {
      const date = new Date(dueDate > 9999999999 ? dueDate : dueDate * 1000);
      date.setHours(0, 0, 0, 0);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    const rawDate = String(dueDate).trim();
    const isoDateMatch = rawDate.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (isoDateMatch) {
      const [, rawYear, rawMonth, rawDay] = isoDateMatch;
      const date = new Date(Number(rawYear), Number(rawMonth) - 1, Number(rawDay));
      date.setHours(0, 0, 0, 0);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    const date = new Date(rawDate);
    date.setHours(0, 0, 0, 0);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const getTodayLocalDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  const isTaskComplete = (task) => task?.status === 'completed';

  const getOverdueTasks = (hire) => {
    if (!hire.tasks) return 0;

    const today = getTodayLocalDate();
    let overdue = 0;

    forEachHireTask(hire, (task) => {
      const dueDate = getTaskLocalDate(task?.dueDate);
      if (!isTaskComplete(task) && dueDate && dueDate < today) {
        overdue++;
      }
    });

    return overdue;
  };

  const getTasksDueToday = (hire) => {
    if (!hire.tasks) return 0;

    const todayTime = getTodayLocalDate().getTime();
    let dueToday = 0;

    forEachHireTask(hire, (task) => {
      const dueDate = getTaskLocalDate(task?.dueDate);
      if (!isTaskComplete(task) && dueDate && dueDate.getTime() === todayTime) {
        dueToday++;
      }
    });

    return dueToday;
  };

  const getTaskCompletionForHire = (hire) => {
    let total = 0;
    let completed = 0;

    forEachHireTask(hire, (task) => {
      total++;
      if (isTaskComplete(task)) completed++;
    });

    return { completed, total };
  };

  const getTaskCompletionForStage = (hire, stageKey) => {
    if (stageKey === 'Completed') {
      return getTaskCompletionForHire(hire);
    }

    let total = 0;
    let completed = 0;

    forEachHireTask(hire, (task, categoryName) => {
      if (categoryName !== stageKey) return;
      total++;
      if (isTaskComplete(task)) completed++;
    });

    return { completed, total };
  };

  const getNextTaskDueDate = (hire) => {
    if (!hire.tasks) return null;

    const today = getTodayLocalDate();
    const upcoming = [];

    forEachHireTask(hire, (task) => {
      if (isTaskComplete(task)) return;
      const dueDate = getTaskLocalDate(task?.dueDate);
      if (dueDate && dueDate >= today) {
        upcoming.push(dueDate);
      }
    });

    upcoming.sort((a, b) => a - b);
    return upcoming[0] || null;
  };

  const handleAddHire = async () => {
    if (tutorialActiveRef.current) {
      return;
    }
    if (!canManageOnboarding) {
      showOnboardingPermissionAlert();
      return;
    }
    if (!newHireForm.name || !newHireForm.email || !newHireForm.startDate) {
      alert(t('alerts.missingHireFields'));
      return;
    }
    if (nationalizationEstablishments.length > 1 && !newHireEstablishmentId) {
      alert(t('alerts.establishmentRequired', 'Select the legal company or branch for this employee.'));
      return;
    }

    try {
      const newHireRef = push(ref(database, `companies/${companyUID}/onboarding`));
      const hireId = newHireRef.key;
      const normalizedHire = {
        ...newHireForm,
        department: normalizeDepartment(newHireForm.department)
      };
      const startDate = new Date(normalizedHire.startDate);
      const hireCreatedDateISO = new Date().toLocaleDateString('en-CA');
      const tasks = {};
      const automatedTasksToSchedule = [];

      let selectedTemplate = null;
      try {
        const templatesRef = ref(database, `companies/${companyUID}/onboardingTemplates`);
        const templatesSnap = await get(templatesRef);
        if (templatesSnap.exists()) {
          const templatesArray = Object.entries(templatesSnap.val()).map(([id, value]) => ({
            id,
            ...value
          }));
          selectedTemplate =
            templatesArray.find(template =>
              template.department &&
              normalizeDepartmentKey(template.department) !== 'all' &&
              matchesDepartment(template.department, normalizedHire.department)
            ) ||
            templatesArray.find(template =>
              normalizeDepartmentKey(template.department) === 'all' ||
              template.isDefault
            ) ||
            templatesArray[0] ||
            null;
        }
      } catch (templateError) {
        console.warn('Template lookup failed, falling back to default templates:', templateError);
      }

      const stageNames = selectedTemplate?.stages
        ? [...new Set([...STAGES, ...Object.keys(selectedTemplate.stages || {})])]
        : STAGES;

      stageNames.forEach(stageName => {
        tasks[stageName] = { workflow: { tasks: {} } };
      });

      const createTaskFromTemplate = (templateTask, stageName, index) => {
        const isAutomated = templateTask?.type === 'automated' || Boolean(templateTask?.automationType);
        const automationType = isAutomated
          ? (templateTask?.automationType || (
            templateTask?.type === 'documentRequest' ? 'document_request' : 'scheduled_email'
          ))
          : null;
        const executionMode = templateTask?.executionMode || 'scheduled';
        let relativeDays = Number.isFinite(Number(templateTask?.relativeDays))
          ? Number(templateTask.relativeDays)
          : Number(templateTask?.defaultDays || 0);
        if (executionMode === 'immediate') {
          relativeDays = 0;
        }
        const taskId = `task_${Date.now()}_${index}_${Math.random().toString(36).slice(2, 10)}`;
        const dueDate = executionMode === 'immediate'
          ? hireCreatedDateISO
          : getTaskDueDate(startDate, relativeDays);
        const requiredDocuments = Array.isArray(templateTask?.requiredDocuments)
          ? templateTask.requiredDocuments.map(doc => String(doc || '').trim()).filter(Boolean)
          : parseRequiredDocuments(templateTask?.requiredDocuments || '');

        const preferredAssignee = templateTask?.assignee || templateTask?.assigneeEmail || templateTask?.assigneeName || '';
        const assigneeCategory = NEW_HIRE_AUTOMATION_TYPES.has(automationType)
          ? 'new_hire'
          : PURE_AUTOMATION_TYPES.has(automationType)
            ? 'automations'
            : (templateTask?.assigneeCategory || inferAssigneeCategory(templateTask));
        const resolvedAssignee = getPersonByIdentifier(preferredAssignee)
          || resolveAssigneeByCategory(assigneeCategory, normalizedHire, selectedTemplate);

        const taskRecord = {
          id: taskId,
          title: templateTask?.title || 'Untitled Task',
          description: templateTask?.description || '',
          dueDate,
          relativeDays,
          status: 'not_started',
          createdAt: Date.now()
        };

        if (requiredDocuments.length > 0) {
          taskRecord.requiredDocuments = requiredDocuments;
          taskRecord.documentCollectionMode = 'candidate_upload';
        }

        if (isAutomated) {
          taskRecord.type = 'automated';
          taskRecord.automationType = automationType;
          taskRecord.executionMode = executionMode;
          taskRecord.assigneeCategory = assigneeCategory;
          if (PURE_AUTOMATION_TYPES.has(automationType)) {
            taskRecord.assignee = 'System';
            taskRecord.assigneeEmail = '';
            taskRecord.assigneeName = 'System (Auto)';
            taskRecord.assigneeDepartment = 'Automations';
            taskRecord.assigneeSource = 'system';
          } else {
            taskRecord.assignee = normalizedHire.email || '';
            taskRecord.assigneeEmail = normalizedHire.email || '';
            taskRecord.assigneeName = normalizedHire.name || 'New Hire';
            taskRecord.assigneeDepartment = 'New Hire';
            taskRecord.assigneeSource = 'new_hire';
          }
          if (templateTask?.emailDraft) taskRecord.emailDraft = templateTask.emailDraft;
          if (templateTask?.emailConfig) taskRecord.emailConfig = templateTask.emailConfig;
          if (templateTask?.formConfig) taskRecord.formConfig = templateTask.formConfig;
          if (templateTask?.meetingConfig) taskRecord.meetingConfig = templateTask.meetingConfig;
          if (templateTask?.attachments) taskRecord.attachments = templateTask.attachments;

          if (automationType === 'form_collect') {
            const token = btoa(`${companyUID}:${hireId}:${stageName}:${taskId}`);
            const link = `${getEmployerAppBaseUrl()}/form-collect/${token}`;
            taskRecord.formLink = link;
            if (taskRecord.emailDraft?.body) {
              taskRecord.emailDraft = {
                ...taskRecord.emailDraft,
                body: taskRecord.emailDraft.body.replace('{{form_link}}', link)
              };
            }
          }

          if (automationType === 'document_request') {
            taskRecord.portalUploadRequired = true;
          }

          automatedTasksToSchedule.push({
            stage: stageName,
            task: taskRecord
          });
        } else {
          taskRecord.type = 'human';
          taskRecord.executionMode = executionMode;
          taskRecord.assigneeCategory = assigneeCategory;
          taskRecord.assignee = resolvedAssignee?.email || '';
          taskRecord.assigneeEmail = resolvedAssignee?.email || '';
          taskRecord.assigneeName = templateTask?.assigneeName || resolvedAssignee?.name || resolvedAssignee?.email || '';
          taskRecord.assigneeDepartment = templateTask?.assigneeDepartment || resolvedAssignee?.department || '';
          taskRecord.assigneeSource = templateTask?.assigneeSource || resolvedAssignee?.source || '';
        }

        return { taskId, taskRecord };
      };

      if (selectedTemplate?.stages) {
        stageNames.forEach((stageName) => {
          const stageData = selectedTemplate.stages?.[stageName];
          const templateTasksForStage = Array.isArray(stageData?.tasks) ? stageData.tasks : [];
          templateTasksForStage.forEach((templateTask, index) => {
            const { taskId, taskRecord } = createTaskFromTemplate(templateTask, stageName, index);
            tasks[stageName].workflow.tasks[taskId] = taskRecord;
          });
        });
      } else {
        Object.entries(taskTemplates).forEach(([stageName, fallbackTemplates]) => {
          fallbackTemplates.forEach((templateTask, index) => {
            const mappedTemplate = { ...templateTask, relativeDays: templateTask.defaultDays || 0 };
            if (templateTask.type === 'documentRequest') {
              mappedTemplate.type = 'automated';
              mappedTemplate.automationType = 'document_request';
            } else if (templateTask.type === 'document' && templateTask.includeEmail) {
              mappedTemplate.type = 'automated';
              mappedTemplate.automationType = 'document_send';
            } else {
              mappedTemplate.type = 'human';
              mappedTemplate.assigneeCategory = inferAssigneeCategory(templateTask);
            }
            const { taskId, taskRecord } = createTaskFromTemplate(mappedTemplate, stageName, index);
            tasks[stageName].workflow.tasks[taskId] = taskRecord;
          });
        });
      }

      const normalizedSource = normalizeAnalyticsSource({
        source: normalizedHire?.primarySource || 'JOBSEEQR',
        sourceCategory: normalizedHire?.sourceCategory || 'platform',
        sourceTags: normalizedHire?.sourceTags || []
      });
      const candidateId = normalizedHire.candidateId || normalizedHire.applicantId || hireId;
      const roleId = normalizedHire.roleId || '';
      const ownerRecruiterId = normalizedHire.ownerRecruiterId || currentUser?.uid || '';
      const ownerRecruiterEmail = normalizedHire.ownerRecruiterEmail || currentUser?.email || '';

      const hireData = {
        ...normalizedHire,
        candidateId,
        roleId,
        ownerRecruiterId,
        ownerRecruiterEmail,
        primarySource: normalizedSource.primarySource,
        sourceCategory: normalizedSource.sourceCategory,
        sourceTags: normalizedSource.sourceTags,
        tasks,
        createdAt: Date.now(),
        createdBy: currentUser.email,
        employeeUID: hireId,
        status: 'onboarding',
        templateUsed: selectedTemplate?.id || 'default_fallback'
      };

      const employeeRecord = buildEmployeeRecordFromHire({
        employeeId: hireId,
        companyUID,
        createdBy: currentUser?.uid,
        createdByEmail: currentUser?.email,
        status: 'onboarding',
        source: 'onboarding_manual',
        hire: {
          name: normalizedHire.name,
          personalEmail: normalizedHire.email,
          role: normalizedHire.role,
          department: normalizedHire.department,
          startDate: normalizedHire.startDate,
          managerUID: normalizedHire.manager,
          buddyMentor: normalizedHire.buddyMentor,
          candidateId,
          roleId,
          ownerRecruiterId,
          ownerRecruiterEmail,
          primarySource: normalizedSource.primarySource,
          sourceCategory: normalizedSource.sourceCategory,
          sourceTags: normalizedSource.sourceTags,
        },
      });
      const roleDepartment = [normalizedHire.role, normalizedHire.department]
        .map(val => (val || '').trim())
        .filter(Boolean)
        .join(' / ');
      if (roleDepartment) {
        employeeRecord.roleDepartment = roleDepartment;
      }
      if (normalizedHire.manager && !employeeRecord.managerId) {
        employeeRecord.managerId = normalizedHire.manager;
      }

      const inviteToken = (window.crypto && typeof window.crypto.randomUUID === 'function')
        ? window.crypto.randomUUID()
        : `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      const portalInviteLink = buildPortalInviteLink(hireId, inviteToken);
      employeeRecord.portal = {
        ...(employeeRecord.portal || {}),
        invite: {
          ...(employeeRecord.portal?.invite || {}),
          token: inviteToken,
          status: 'pending',
          generatedAt: Date.now(),
          sentAt: Date.now(),
          invitedBy: currentUser?.email || '',
          inviteEmail: normalizedHire.email
        }
      };

      const updates = {};
      updates[`companies/${companyUID}/onboarding/${hireId}`] = hireData;
      updates[`companies/${companyUID}/employees/${hireId}`] = employeeRecord;

      // The marker is committed atomically with the hire. If the API or browser
      // stops immediately afterwards, background replay still has durable work.
      const lifecycleEvent = addNationalizationLifecycleEventToUpdates(updates, {
        companyUID,
        employeeId: hireId,
        source: 'jobseeqr_hire',
        changeKinds: ['lifecycle'],
        establishmentId: newHireEstablishmentId || undefined
      });

      await update(ref(database), updates);
      let lifecycleSync = null;
      try {
        lifecycleSync = await nationalizationService.syncEmployeeLifecycle(companyUID, hireId, {
          eventId: lifecycleEvent.eventId,
          source: 'jobseeqr_hire',
          changeKinds: ['lifecycle'],
          establishmentId: newHireEstablishmentId || undefined
        });
      } catch (syncError) {
        // Hiring still succeeds. The nationalization screen will show this
        // worker as needing regulatory completion instead of claiming a count.
        console.warn('Nationalization lifecycle sync is pending for the new hire:', syncError);
      }
      announceNationalizationWorkforceChange({
        companyUID,
        source: 'jobseeqr_hire',
        recalculated: false,
        requiresRegulatoryCompletion: lifecycleSync?.requires_regulatory_completion !== false,
        syncPending: !lifecycleSync
      });

      const authToken = await getCurrentAuthToken();
      if (authToken) {
        const documentLinkUpdates = {};
        for (const [stageName, stageData] of Object.entries(tasks)) {
          const workflowTasks = stageData?.workflow?.tasks || {};
          for (const [taskId, taskRecord] of Object.entries(workflowTasks)) {
            if (taskRecord?.type !== 'automated' || taskRecord?.automationType !== 'document_request') continue;
            const uploadLink = await generateDocumentUploadPublicLink({
              hireId,
              stage: stageName,
              taskId,
              authToken
            });
            if (!uploadLink) continue;
            taskRecord.documentUploadLink = uploadLink;
            taskRecord.portalUploadRequired = true;
            documentLinkUpdates[`companies/${companyUID}/onboarding/${hireId}/tasks/${stageName}/workflow/tasks/${taskId}/documentUploadLink`] = uploadLink;
            documentLinkUpdates[`companies/${companyUID}/onboarding/${hireId}/tasks/${stageName}/workflow/tasks/${taskId}/portalUploadRequired`] = true;
            if (taskRecord.emailDraft?.body) {
              taskRecord.emailDraft = {
                ...taskRecord.emailDraft,
                body: taskRecord.emailDraft.body
                  .replace('{{portal_link}}', uploadLink)
                  .replace('{{document_upload_link}}', uploadLink)
              };
              documentLinkUpdates[`companies/${companyUID}/onboarding/${hireId}/tasks/${stageName}/workflow/tasks/${taskId}/emailDraft`] = taskRecord.emailDraft;
            }
          }
        }
        if (Object.keys(documentLinkUpdates).length > 0) {
          await update(ref(database), documentLinkUpdates);
        }
      }

      // Send welcome email notification
      if (normalizedHire.manager) {
        // Auto-promote the assigned manager to have 'manager' permission in portal
        try {
          const managerPermRef = ref(database, `companies/${companyUID}/employees/${normalizedHire.manager}/portal/permissions`);
          get(managerPermRef).then((snap) => {
            const perms = snap.val() || ['employee'];
            if (!perms.includes('manager')) {
              const newPerms = [...perms, 'manager'];
              update(ref(database), {
                [`companies/${companyUID}/employees/${normalizedHire.manager}/portal/permissions`]: newPerms
              });
            }
          });
        } catch (err) {
          console.error("Error auto-promoting manager:", err);
        }

        await sendTaskNotification({
          recipientEmail: normalizedHire.manager,
          recipientName: normalizedHire.manager,
          subject: t('notifications.managerSubject', { name: normalizedHire.name }),
          message: t('notifications.managerMessage', { name: normalizedHire.name, startDate: formatDate(normalizedHire.startDate) })
        });
      }

      await sendTaskNotification({
        recipientEmail: normalizedHire.email,
        recipientName: normalizedHire.name,
        subject: `Welcome to your employee portal, ${normalizedHire.name.split(' ')[0] || normalizedHire.name}`,
        message: `Your onboarding portal is ready.\n\nUse the secure link below to activate access and complete required onboarding steps.`,
        showDashboardButton: false,
        actionButtonLabel: 'Open Employee Portal',
        actionButtonUrl: portalInviteLink
      });

      if (authToken && automatedTasksToSchedule.length > 0) {
        try {
          await Promise.all(
            automatedTasksToSchedule.map(async ({ stage, task }) => {
              await fetch(`${BACKEND_URL}/onboarding/workflow/schedule-automation`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify({
                  companyUID,
                  hireId,
                  taskId: task.id,
                  automationType: task.automationType,
                  executeAt: task.executionMode === 'immediate' ? null : task.dueDate,
	                  payload: {
	                    stage,
	                    title: task.title,
	                    emailDraft: task.emailDraft || null,
	                    emailConfig: task.emailConfig || null,
	                    executionMode: task.executionMode || 'scheduled',
	                    formConfig: task.formConfig || null,
                    meetingConfig: task.meetingConfig || null,
                    requiredDocuments: task.requiredDocuments || [],
                    documentUploadLink: task.documentUploadLink || null,
                    attachments: task.attachments || []
                  }
                })
              });
            })
          );
        } catch (scheduleError) {
          console.error('Failed to schedule one or more onboarding automations:', scheduleError);
        }
      }

      const assignmentNotifications = [];
      Object.entries(tasks).forEach(([stageName, stageData]) => {
        const workflowTasks = stageData?.workflow?.tasks || {};
        Object.entries(workflowTasks).forEach(([taskId, task]) => {
          if (task?.type === 'human' && task?.assignee) {
            assignmentNotifications.push(
              notifyTaskAssignee({
                assigneeEmail: task.assignee,
                task,
                hire: { ...normalizedHire, id: hireId },
                stage: stageName,
                taskId,
                authToken
              })
            );
          }
        });
      });
      if (assignmentNotifications.length > 0) {
        await Promise.all(assignmentNotifications);
      }

      // Email reminders are handled automatically by daily digest
      // No need to manually schedule reminders for each task

      setShowAddHireModal(false);
      setNewHireForm({
        name: '',
        email: '',
        role: '',
        department: '',
        startDate: '',
        manager: '',
        buddyMentor: ''
      });
      setNewHireEstablishmentId(
        nationalizationEstablishments.length === 1 ? nationalizationEstablishments[0].id : ''
      );

      alert(t('alerts.hireAdded'));
    } catch (error) {
      console.error('Error adding new hire:', error);
      alert(t('alerts.hireAddFailed'));
    }
  };

  const updateTaskStatus = async (hireId, category, taskId, newStatus) => {
    if (tutorialActiveRef.current) {
      return;
    }
    try {
      // Determine path based on data structure
      const hire = newHires.find(h => h.id === hireId);
      const isWorkflowTask = hire?.tasks?.[category]?.workflow?.tasks?.[taskId];
      const task = hire?.tasks?.[category]?.workflow?.tasks?.[taskId]
        || hire?.tasks?.[category]?.[taskId];

      if (!canUpdateTaskStatus(task, newStatus)) {
        showOnboardingPermissionAlert();
        return;
      }

      const path = isWorkflowTask
        ? `companies/${companyUID}/onboarding/${hireId}/tasks/${category}/workflow/tasks/${taskId}/status`
        : `companies/${companyUID}/onboarding/${hireId}/tasks/${category}/${taskId}/status`;

      const taskRef = ref(database, path);
      await set(taskRef, newStatus);

      // Update local state
      setSelectedHire(prev => {
        if (!prev || prev.id !== hireId) return prev;

        const updatedTasks = { ...prev.tasks };
        const categoryData = updatedTasks[category] ? { ...updatedTasks[category] } : null;

        if (categoryData && categoryData.workflow && categoryData.workflow.tasks && categoryData.workflow.tasks[taskId]) {
          // Workflow path
          categoryData.workflow = { ...categoryData.workflow };
          categoryData.workflow.tasks = { ...categoryData.workflow.tasks };
          categoryData.workflow.tasks[taskId] = {
            ...categoryData.workflow.tasks[taskId],
            status: newStatus
          };
        } else if (categoryData && categoryData[taskId]) {
          // Legacy path
          categoryData[taskId] = {
            ...categoryData[taskId],
            status: newStatus
          };
        }

        if (categoryData) {
          updatedTasks[category] = categoryData;
        }

        return {
          ...prev,
          tasks: updatedTasks
        };
      });
    } catch (error) {
      console.error('Error updating task status:', error);
      alert(t('alerts.updateTaskFailed'));
    }
  };

  const updateTaskField = async (hireId, category, taskId, field, value) => {
    if (tutorialActiveRef.current) {
      return;
    }
    if (!canManageOnboarding) {
      showOnboardingPermissionAlert();
      return;
    }
    try {
      // Determine path based on data structure
      const hire = newHires.find(h => h.id === hireId);
      const isWorkflowTask = hire?.tasks?.[category]?.workflow?.tasks?.[taskId];

      const path = isWorkflowTask
        ? `companies/${companyUID}/onboarding/${hireId}/tasks/${category}/workflow/tasks/${taskId}/${field}`
        : `companies/${companyUID}/onboarding/${hireId}/tasks/${category}/${taskId}/${field}`;

      const taskRef = ref(database, path);
      await set(taskRef, value);

      const task = hire?.tasks?.[category]?.workflow?.tasks?.[taskId]
        || hire?.tasks?.[category]?.[taskId];

      // Update local state immediately to reflect changes in UI
      setSelectedHire(prev => {
        if (!prev || prev.id !== hireId) return prev;

        const updatedTasks = { ...prev.tasks };
        const categoryData = updatedTasks[category] ? { ...updatedTasks[category] } : null;

        if (categoryData && categoryData.workflow && categoryData.workflow.tasks && categoryData.workflow.tasks[taskId]) {
          // Workflow path
          categoryData.workflow = { ...categoryData.workflow };
          categoryData.workflow.tasks = { ...categoryData.workflow.tasks };
          categoryData.workflow.tasks[taskId] = {
            ...categoryData.workflow.tasks[taskId],
            [field]: value
          };
        } else if (categoryData && categoryData[taskId]) {
          // Legacy path
          categoryData[taskId] = {
            ...categoryData[taskId],
            [field]: value
          };
        }

        if (categoryData) {
          updatedTasks[category] = categoryData;
        }

        return {
          ...prev,
          tasks: updatedTasks
        };
      });

      // If assignee changed, send notification
      if (field === 'assignee' && value) {
        if (task) {
          const authToken = await getCurrentAuthToken();
          await notifyTaskAssignee({
            assigneeEmail: value,
            task,
            hire: { ...hire, id: hireId },
            stage: category,
            taskId,
            authToken
          });
        }
      }

      // Email reminders are handled automatically by daily digest
      // No need to manually reschedule reminders when due dates or assignees change

    } catch (error) {
      console.error('Error updating task field:', error);
      alert(t('alerts.updateTaskFailed'));
    }
  };

  const deleteTask = async (hireId, category, taskId) => {
    if (tutorialActiveRef.current) {
      return;
    }
    if (!canManageOnboarding) {
      showOnboardingPermissionAlert();
      return;
    }
    if (!window.confirm(t('alerts.deleteTaskConfirm'))) {
      return;
    }

    try {
      // Determine path
      const hire = newHires.find(h => h.id === hireId);
      const isWorkflowTask = hire?.tasks?.[category]?.workflow?.tasks?.[taskId];

      const path = isWorkflowTask
        ? `companies/${companyUID}/onboarding/${hireId}/tasks/${category}/workflow/tasks/${taskId}`
        : `companies/${companyUID}/onboarding/${hireId}/tasks/${category}/${taskId}`;

      const taskRef = ref(database, path);
      await remove(taskRef);

      // Email reminders are handled automatically by daily digest
      // Deleted tasks won't receive reminders (daily digest checks for existing tasks only)

      // Update local state (remove task)
      setSelectedHire(prev => {
        if (!prev || prev.id !== hireId) return prev;

        const updatedTasks = { ...prev.tasks };
        const categoryData = updatedTasks[category] ? { ...updatedTasks[category] } : null;

        if (categoryData) {
          if (categoryData.workflow && categoryData.workflow.tasks && categoryData.workflow.tasks[taskId]) {
            // Workflow path removal
            categoryData.workflow = { ...categoryData.workflow };
            categoryData.workflow.tasks = { ...categoryData.workflow.tasks };
            delete categoryData.workflow.tasks[taskId];
          } else if (categoryData[taskId]) {
            // Legacy path removal
            delete categoryData[taskId];
          }
          updatedTasks[category] = categoryData;
        }

        return {
          ...prev,
          tasks: updatedTasks
        };
      });

    } catch (error) {
      console.error('Error deleting task:', error);
      alert(t('alerts.deleteTaskFailed'));
    }
  };

  const sendTaskNotification = async (emailData) => {
    try {
      const response = await fetch(`${BACKEND_URL}/send-onboarding-notification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...emailData,
          // showDashboardButton defaults to true unless explicitly set to false
          showDashboardButton: emailData.showDashboardButton !== undefined ? emailData.showDashboardButton : true
        })
      });

      if (!response.ok) {
        console.error('Failed to send notification email');
      }
    } catch (error) {
      console.error('Error sending notification:', error);
    }
  };

  const isExternalContactEmail = (email) => {
    const normalized = normalizeText(email);
    return externalContacts.some(contact => normalizeText(contact?.email) === normalized);
  };

  const notifyTaskAssignee = async ({
    assigneeEmail,
    task,
    hire,
    stage,
    taskId,
    authToken
  }) => {
    if (!assigneeEmail || !task) return;
    const isExternal = isExternalContactEmail(assigneeEmail) || task?.assigneeSource === 'template_team';
    let actionButtonUrl = null;
    if (isExternal) {
      actionButtonUrl = await generateExternalTaskCompletionLink({
        hireId: hire?.id,
        stage,
        taskId,
        assigneeEmail,
        authToken
      });
    }

    const fallbackMessage = `You have been assigned a task for ${(hire?.name || selectedHire?.name || 'new hire')}'s onboarding: ${task.title}. Due date: ${task.dueDate || 'Not set'}`;
    const configuredSubject = task?.emailDraft?.subject?.trim();
    const configuredBody = task?.emailDraft?.body?.trim();
    const plainFromHtml = configuredBody
      ? configuredBody.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
      : '';

    await sendTaskNotification({
      recipientEmail: assigneeEmail,
      recipientName: getPersonByEmail(assigneeEmail)?.name || assigneeEmail,
      subject: configuredSubject || `Task Assigned: ${task.title}`,
      message: plainFromHtml || fallbackMessage,
      messageHtml: configuredBody || undefined,
      showDashboardButton: !isExternal,
      actionButtonLabel: isExternal && actionButtonUrl ? 'Mark Task Complete' : undefined,
      actionButtonUrl: isExternal ? actionButtonUrl : undefined
    });
  };

  // ============================================================================
  // NOTE: Email reminders are now handled automatically by daily digest!
  // No need to schedule individual jobs for each task.
  // The backend runs a daily job that checks all tasks and sends reminders.
  // This saves ~$150/month in Cloud Scheduler costs and is more reliable.

  // Generate email content using AI
  const generateEmailWithAI = async () => {
    if (!canManageOnboarding) {
      showOnboardingPermissionAlert();
      return;
    }
    try {
      const response = await fetch(`${BACKEND_URL}/onboarding/generate-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          taskTitle: taskForm.title,
          taskDescription: taskForm.description,
          hireName: selectedHire?.name,
          hireRole: selectedHire?.role,
          startDate: selectedHire?.startDate,
          companyName: '' // Could be populated from company data if available
        })
      });

      if (response.ok) {
        const { subject, body } = await response.json();
        setTaskForm(prev => ({
          ...prev,
          emailConfig: {
            ...prev.emailConfig,
            subject: subject || prev.emailConfig.subject,
            body: body || prev.emailConfig.body
          }
        }));
      } else {
        console.error('Failed to generate email with AI');
        alert(uiText('Failed to generate email. Please try again or draft manually.'));
      }
    } catch (error) {
      console.error('Error generating email with AI:', error);
      alert(uiText('Error generating email. Please try again or draft manually.'));
    }
  };
  // ============================================================================

  // Generate email template based on task
  const generateEmailTemplate = (task, hireName) => {
    const taskTitle = (task.title || '').toLowerCase();
    const startDate = new Date(selectedHire.startDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    startDate.setHours(0, 0, 0, 0);
    const daysUntilStart = Math.ceil((startDate - today) / (1000 * 60 * 60 * 24));
    const hasStarted = daysUntilStart < 0;
    const isToday = daysUntilStart === 0;
    const startDateFormatted = formatDate(selectedHire.startDate);

    // Timing message
    let timingMessage = '';
    if (isToday) {
      timingMessage = t('emailTemplates.timing.today');
    } else if (hasStarted) {
      timingMessage = t('emailTemplates.timing.started', { count: Math.abs(daysUntilStart) });
    } else if (daysUntilStart === 1) {
      timingMessage = t('emailTemplates.timing.tomorrow');
    } else if (daysUntilStart <= 7) {
      timingMessage = t('emailTemplates.timing.withinWeek', { count: daysUntilStart, startDate: startDateFormatted });
    } else {
      timingMessage = t('emailTemplates.timing.scheduled', { startDate: startDateFormatted });
    }

    const baseReplacements = {
      name: hireName,
      timingMessage,
      startDate: startDateFormatted,
      description: task.description || ''
    };

    if (taskTitle.includes('welcome email')) {
      return {
        subject: t('emailTemplates.welcome.subject', { name: hireName }),
        message: t('emailTemplates.welcome.message', {
          ...baseReplacements,
          prepNote: hasStarted ? t('emailTemplates.welcome.startedNote') : t('emailTemplates.welcome.prepNote')
        })
      };
    }

    if (taskTitle.includes('handbook')) {
      return {
        subject: t('emailTemplates.handbook.subject'),
        message: t('emailTemplates.handbook.message', {
          ...baseReplacements,
          timingNote: hasStarted
            ? t('emailTemplates.handbook.afterStart')
            : t('emailTemplates.handbook.beforeStart', { startDate: startDateFormatted })
        })
      };
    }

    if (taskTitle.includes('welcome kit')) {
      const kitStatus = !hasStarted && !isToday
        ? t('emailTemplates.welcomeKit.readyForDay', { startDate: startDateFormatted, count: daysUntilStart })
        : hasStarted
          ? t('emailTemplates.welcomeKit.readyAtDesk')
          : t('emailTemplates.welcomeKit.readyToday');

      return {
        subject: t('emailTemplates.welcomeKit.subject'),
        message: t('emailTemplates.welcomeKit.message', {
          ...baseReplacements,
          status: kitStatus
        })
      };
    }

    if (taskTitle.includes('check-in') || taskTitle.includes('feedback')) {
      const checkInContext = hasStarted
        ? t('emailTemplates.checkIn.context.started')
        : isToday
          ? t('emailTemplates.checkIn.context.today')
          : t('emailTemplates.checkIn.context.before', { startDate: startDateFormatted });

      return {
        subject: t('emailTemplates.checkIn.subject'),
        message: t('emailTemplates.checkIn.message', {
          ...baseReplacements,
          context: checkInContext
        })
      };
    }

    if (taskTitle.includes('pre-boarding') || taskTitle.includes('documents')) {
      const urgencyNote = hasStarted
        ? t('emailTemplates.preboarding.urgency.started')
        : isToday
          ? t('emailTemplates.preboarding.urgency.today')
          : daysUntilStart === 1
            ? t('emailTemplates.preboarding.urgency.tomorrow')
            : daysUntilStart <= 7
              ? t('emailTemplates.preboarding.urgency.withinWeek', { startDate: startDateFormatted, count: daysUntilStart })
              : t('emailTemplates.preboarding.urgency.scheduled', { startDate: startDateFormatted });

      return {
        subject: t('emailTemplates.preboarding.subject'),
        message: t('emailTemplates.preboarding.message', {
          ...baseReplacements,
          urgency: urgencyNote
        })
      };
    }

    if (task.type === 'contract') {
      return {
        subject: t('emailTemplates.contract.subject'),
        message: t('emailTemplates.contract.message', {
          ...baseReplacements,
          role: selectedHire.role,
          department: selectedHire.department || t('labels.unspecified'),
          manager: selectedHire.manager || t('labels.unspecified')
        })
      };
    }

    if (task.type === 'document') {
      return {
        subject: t('emailTemplates.document.subject', { title: task.title }),
        message: t('emailTemplates.document.message', {
          ...baseReplacements,
          title: task.title
        })
      };
    }

    return {
      subject: t('emailTemplates.default.subject', { title: task.title }),
      message: t('emailTemplates.default.message', {
        ...baseReplacements,
        title: task.title
      })
    };
  };

  // Open email preview modal
  const openEmailPreview = (to, toName, subject, message, task = null) => {
    if (!canManageOnboarding) {
      showOnboardingPermissionAlert();
      return;
    }
    const normalizedMessage = String(message || '');
    const isLikelyHtml = /<\/?[a-z][\s\S]*>/i.test(normalizedMessage);
    setEmailDraft({
      to,
      toName,
      subject,
      message: isLikelyHtml ? normalizedMessage : normalizedMessage.replace(/\n/g, '<br>'),
      task  // Store task reference to access attachments later
    });
    setShowEmailPreview(true);
  };

  // Send email from preview modal with attachments
  const handleSendEmail = async () => {
    if (tutorialActiveRef.current) {
      return;
    }
    if (!canManageOnboarding) {
      showOnboardingPermissionAlert();
      return;
    }
    try {
      // Collect attachments for the current task if any
      const attachments = [];

      // Get the current task (stored in emailDraft.task if available)
      const currentTask = emailDraft.task;

      if (currentTask) {
        // Handle document task attachments
        if (currentTask.attachments && currentTask.attachments.length > 0) {
          currentTask.attachments.forEach(att => {
            attachments.push({
              name: att.name,
              url: att.url,
              type: 'document'
            });
          });
        }

        // Handle contract task - generate PDF and attach
        if (currentTask.type === 'contract' && currentTask.contractTemplateId) {
          const template = contractTemplates.find(t => t.id === currentTask.contractTemplateId);
          if (template) {
            // Use the pre-generated PDF URL from template if available
            if (template.downloadURL) {
              attachments.push({
                name: `${template.title || 'Contract'}.pdf`,
                url: template.downloadURL,
                type: 'contract'
              });
            } else if (currentTask.filledContractHTML) {
              // If we have filled HTML but no PDF, we should generate one
              // For now, log a warning - in production, you'd want to generate the PDF
              console.warn('Contract has filled HTML but no PDF URL. Consider generating PDF on task creation.');
            }
          }
        }
      }

      await sendTaskNotification({
        recipientEmail: emailDraft.to,
        recipientName: emailDraft.toName,
        subject: emailDraft.subject,
        message: String(emailDraft.message || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim(),
        messageHtml: emailDraft.message,
        showDashboardButton: false,  // Don't show dashboard button for emails to new hires
        attachments: attachments.length > 0 ? attachments : undefined
      });
      setShowEmailPreview(false);
      alert(t('alerts.emailSent', { name: emailDraft.toName }));
    } catch (error) {
      console.error('Error sending email:', error);
      alert(t('alerts.emailSendFailed'));
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const locale = i18n.language === 'ar' ? 'ar-EG' : 'en-US';
    return date.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCardDueDate = (date) => {
    if (!date) return t('labels.noUpcomingTask');
    if (i18n.language === 'ar') {
      return date.toLocaleDateString('ar-EG', { month: 'long', day: 'numeric' });
    }

    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const suffix = (day % 100 >= 11 && day % 100 <= 13)
      ? 'th'
      : ({ 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th');

    return `${month} ${day}${suffix}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#4CAF50';
      case 'in_progress': return '#2196F3';
      case 'not_started': return '#9E9E9E';
      default: return '#9E9E9E';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'fa-check-circle';
      case 'in_progress': return 'fa-spinner';
      case 'not_started': return 'fa-circle';
      default: return 'fa-circle';
    }
  };

  // Load contract templates
  const loadContractTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const templatesRef = ref(database, `companies/${companyUID}/contracts/docs`);
      const snapshot = await get(templatesRef);

      if (snapshot.exists()) {
        const templatesData = snapshot.val();
        const templatesList = Object.entries(templatesData).map(([id, data]) => ({
          id,
          ...data
        }));
        setContractTemplates(templatesList);
      }
    } catch (error) {
      console.error('Error loading contract templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Load contract templates when in detail view
  useEffect(() => {
    if (tutorialActive) {
      return;
    }
    if (companyUID && selectedHire) {
      loadContractTemplates();
    }
  }, [companyUID, selectedHire, tutorialActive]);

  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: prev[category] === false
    }));
  };

  // Auto-default email scheduled time to 9 AM on due date when email is enabled
  useEffect(() => {
    if (taskForm.includeEmail && taskForm.dueDate && !taskForm.emailConfig.scheduledFor) {
      // Parse due date and set to 9 AM local time
      const dueDate = new Date(taskForm.dueDate + 'T09:00:00');

      // Format for datetime-local input (YYYY-MM-DDTHH:MM)
      const year = dueDate.getFullYear();
      const month = String(dueDate.getMonth() + 1).padStart(2, '0');
      const day = String(dueDate.getDate()).padStart(2, '0');
      const localDateTime = `${year}-${month}-${day}T09:00`;

      setTaskForm(prev => ({
        ...prev,
        emailConfig: {
          ...prev.emailConfig,
          scheduledFor: localDateTime,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        }
      }));
    }
  }, [taskForm.includeEmail, taskForm.dueDate]);

  // Handle file selection for document task
  const handleTaskFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setTaskForm(prev => ({ ...prev, uploadedFile: file }));
    }
  };

  // Detect placeholders in contract template HTML
  const detectPlaceholders = (htmlContent) => {
    if (!htmlContent) return [];

    const placeholderPatterns = [
      /\[([^\]]+)\]/g,              // [PLACEHOLDER]
      /\{([^}]+)\}/g,               // {PLACEHOLDER}
      /\{\{([^}]+)\}\}/g,           // {{PLACEHOLDER}}
      /___+([^_\s]+)___+/g,         // ___PLACEHOLDER___
      /\$\{([^}]+)\}/g,             // ${PLACEHOLDER}
    ];

    const placeholders = new Set();

    placeholderPatterns.forEach(pattern => {
      let match;
      const regex = new RegExp(pattern);
      while ((match = regex.exec(htmlContent)) !== null) {
        const placeholder = match[1].trim();
        // Filter out HTML tags and common non-placeholder content
        if (!placeholder.match(/^(\/|!|html|head|body|div|span|p|h[1-6]|br|hr|table|tr|td|th|ul|ol|li|strong|em|b|i|u|style|script|link|meta|title)$/i) &&
          placeholder.length > 0 &&
          placeholder.length < 100) {
          placeholders.add(placeholder);
        }
      }
    });

    return Array.from(placeholders);
  };

  // Create new task
  const handleCreateTask = async () => {
    if (tutorialActiveRef.current) {
      return;
    }
    if (!canManageOnboarding) {
      showOnboardingPermissionAlert();
      return;
    }
    if (!taskForm.title.trim()) {
      alert(t('alerts.enterTaskTitle'));
      return;
    }

    // If contract type with template, check for placeholders first
    if (taskForm.type === 'contract' && taskForm.contractTemplateId) {
      const template = contractTemplates.find(t => t.id === taskForm.contractTemplateId);
      if (template && template.HTMLContract) {
        const placeholders = detectPlaceholders(template.HTMLContract);

        if (placeholders.length > 0) {
          // Store task data and show placeholder modal
          setCurrentTaskForPlaceholders({
            ...taskForm,
            taskId: `task_${Date.now()}`,
            template: template
          });
          setDetectedPlaceholders(placeholders);

          // Pre-fill some placeholders with hire data
          const preFilled = {};
          placeholders.forEach(ph => {
            const phLower = ph.toLowerCase();
            if (phLower.includes('name') || phLower.includes('employee')) {
              preFilled[ph] = selectedHire.name;
            } else if (phLower.includes('email')) {
              preFilled[ph] = selectedHire.email;
            } else if (phLower.includes('role') || phLower.includes('position') || phLower.includes('title')) {
              preFilled[ph] = selectedHire.role;
            } else if (phLower.includes('department')) {
              preFilled[ph] = selectedHire.department || '';
            } else if (phLower.includes('start') && phLower.includes('date')) {
              preFilled[ph] = formatDate(selectedHire.startDate);
            } else if (phLower.includes('manager')) {
              preFilled[ph] = selectedHire.manager || '';
            }
          });
          setPlaceholderValues(preFilled);
          setShowPlaceholderModal(true);
          return;
        }
      }
    }

    // Continue with task creation (no placeholders or not contract type)
    await finalizeTaskCreation(taskForm, `task_${Date.now()}`);
  };

  // Finalize task creation after placeholder filling (or direct creation)
  const finalizeTaskCreation = async (taskData, taskId, filledHtml = null, pdfUrl = null) => {
    if (!canManageOnboarding) {
      showOnboardingPermissionAlert();
      return;
    }
    try {
      // Assignees are now directly in the array (unified dropdown includes New Hire, Manager, IT, team)
      const allAssignees = [...(taskData.assignees || [])];

      const newTask = {
        title: taskData.title,
        description: taskData.description,
        dueDate: taskData.dueDate || new Date().toISOString().split('T')[0],
        assignees: allAssignees,  // Now an array
        status: 'not_started',
        createdAt: Date.now(),
        type: taskData.type
      };
      const requiredDocuments = Array.isArray(taskData.requiredDocuments)
        ? taskData.requiredDocuments
        : parseRequiredDocuments(taskData.requiredDocumentsText);
      if (requiredDocuments.length > 0) {
        newTask.requiredDocuments = requiredDocuments;
        newTask.documentCollectionMode = 'candidate_upload';
      }

      // Handle contract template reference
      if (taskData.type === 'contract' && taskData.contractTemplateId) {
        newTask.contractTemplateId = taskData.contractTemplateId;
        if (filledHtml) {
          newTask.filledContractHTML = filledHtml;
        }
        // Add PDF URL as attachment if generated
        if (pdfUrl) {
          newTask.attachments = [{
            name: `${taskData.title || 'Contract'}.pdf`,
            url: pdfUrl,
            uploadedAt: Date.now()
          }];
        }
      }

      // Handle document upload
      if (taskData.type === 'document' && taskData.uploadedFile) {
        const filePath = `onboarding_tasks/${companyUID}/${selectedHire.id}/${taskId}/${taskData.uploadedFile.name}`;
        const fileRef = storageRef(storage, filePath);
        await uploadBytes(fileRef, taskData.uploadedFile);
        const fileURL = await getDownloadURL(fileRef);

        newTask.attachments = [{
          name: taskData.uploadedFile.name,
          url: fileURL,
          uploadedAt: Date.now()
        }];
      }

      // Handle document request (link to employee portal)
      if (taskData.type === 'documentRequest') {
        newTask.portalUploadRequired = true;
        newTask.documentTypes = requiredDocuments;
      }

      // Save task to database
      const taskPath = `companies/${companyUID}/onboarding/${selectedHire.id}/tasks/${taskCategory}/${taskId}`;
      await set(ref(database, taskPath), newTask);

      const authToken = await getCurrentAuthToken();
      if (taskData.type === 'documentRequest' && authToken) {
        const uploadLink = await generateDocumentUploadPublicLink({
          hireId: selectedHire.id,
          stage: taskCategory,
          taskId,
          authToken
        });
        if (uploadLink) {
          newTask.documentUploadLink = uploadLink;
          await update(ref(database, taskPath), {
            documentUploadLink: uploadLink,
            portalUploadRequired: true
          });
        }
      }

      // Send email notification to ALL assignees
      if (allAssignees.length > 0) {
        const notificationPromises = allAssignees.map(assigneeEmail => (
          notifyTaskAssignee({
            assigneeEmail,
            task: newTask,
            hire: selectedHire,
            stage: taskCategory,
            taskId,
            authToken
          })
        ));
        await Promise.all(notificationPromises);
      }

      // Handle scheduled email if configured
      if (taskData.includeEmail && taskData.emailConfig?.scheduledFor) {
        const scheduledEmailPath = `companies/${companyUID}/scheduled_emails/${taskId}_email`;
        await set(ref(database, scheduledEmailPath), {
          taskId,
          hireId: selectedHire.id,
          hireName: selectedHire.name,
          hireEmail: selectedHire.email,
          subject: taskData.emailConfig.subject,
          body: taskData.emailConfig.body,
          scheduledFor: taskData.emailConfig.scheduledFor,
          timezone: taskData.emailConfig.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          recipients: taskData.emailConfig.recipients,
          assignees: allAssignees, // Include assignees for 'assignees' recipient type
          status: 'pending',
          createdAt: Date.now()
        });
      }

      // Update local state
      setSelectedHire(prev => ({
        ...prev,
        tasks: {
          ...prev.tasks,
          [taskCategory]: {
            ...(prev.tasks?.[taskCategory] || {}),
            [taskId]: newTask
          }
        }
      }));

      // Email reminders are handled automatically by daily digest
      // No need to manually schedule reminders for each task

      setShowAddTaskModal(false);
      alert(t('alerts.taskCreated'));
    } catch (error) {
      console.error('Error creating task:', error);
      alert(t('alerts.createTaskFailed'));
    }
  };

  // Handle placeholder modal submission
  const handlePlaceholderSubmit = async () => {
    if (!currentTaskForPlaceholders) return;
    if (!canManageOnboarding) {
      showOnboardingPermissionAlert();
      return;
    }

    try {
      // Fill placeholders in HTML
      let filledHtml = currentTaskForPlaceholders.template.HTMLContract;
      Object.entries(placeholderValues).forEach(([placeholder, value]) => {
        if (value) {
          const patterns = [
            new RegExp(`\\[${placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\]`, 'gi'),
            new RegExp(`\\{${placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\}`, 'gi'),
            new RegExp(`\\{\\{${placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\}\\}`, 'gi'),
            new RegExp(`___+${placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}___+`, 'gi'),
            new RegExp(`\\$\\{${placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\}`, 'gi'),
          ];

          patterns.forEach(pattern => {
            filledHtml = filledHtml.replace(pattern, value);
          });
        }
      });

      // Generate PDF from filled HTML
      let pdfUrl = null;
      try {
        const pdfResponse = await fetch(`${BACKEND_URL}/contracts/ai/render-pdf`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            html: filledHtml,
            title: currentTaskForPlaceholders.template.title || 'Contract',
            page_size: 'A4'
          })
        });

        if (pdfResponse.ok) {
          const pdfBlob = await pdfResponse.blob();

          // Upload PDF to Firebase Storage
          const taskId = currentTaskForPlaceholders.taskId;
          const pdfPath = `onboarding_tasks/${companyUID}/${selectedHire.id}/${taskId}/contract.pdf`;
          const pdfStorageRef = storageRef(storage, pdfPath);
          await uploadBytes(pdfStorageRef, pdfBlob);
          pdfUrl = await getDownloadURL(pdfStorageRef);

          console.log('✅ PDF generated and uploaded for contract:', pdfUrl);
        } else {
          console.warn('⚠️ PDF generation failed, will store HTML only');
        }
      } catch (pdfError) {
        console.error('Error generating PDF for contract:', pdfError);
        // Continue without PDF - HTML will still be stored
      }

      // Create the task with filled HTML and PDF URL
      await finalizeTaskCreation(currentTaskForPlaceholders, currentTaskForPlaceholders.taskId, filledHtml, pdfUrl);

      // Reset state
      setShowPlaceholderModal(false);
      setCurrentTaskForPlaceholders(null);
      setPlaceholderValues({});
      setDetectedPlaceholders([]);
    } catch (error) {
      console.error('Error in placeholder submit:', error);
      alert(t('alerts.createContractTaskFailed'));
    }
  };

  // Save current tasks as department template
  const saveDepartmentTemplate = async () => {
    if (tutorialActiveRef.current) {
      return;
    }
    if (!canManageOnboarding) {
      showOnboardingPermissionAlert();
      return;
    }
    const deptKey = normalizeDepartment(selectedHire?.department);
    if (!deptKey) {
      alert(t('alerts.noDepartment'));
      return;
    }

    const deptLabel = getDepartmentLabel(deptKey);

    if (!window.confirm(t('alerts.saveTemplateConfirm', { department: deptLabel }))) {
      return;
    }

    try {
      const templateData = selectedHire.tasks || {};
      const templatePath = `companies/${companyUID}/onboarding_templates/${deptKey}`;
      await set(ref(database, templatePath), templateData);
      alert(t('alerts.saveTemplateSuccess', { department: deptLabel }));
    } catch (error) {
      console.error('Error saving department template:', error);
      alert(t('alerts.saveTemplateFailed'));
    }
  };

  // Apply department template to current hire
  const applyDepartmentTemplate = async () => {
    if (tutorialActiveRef.current) {
      return;
    }
    if (!canManageOnboarding) {
      showOnboardingPermissionAlert();
      return;
    }
    const deptKey = normalizeDepartment(selectedHire?.department);
    if (!deptKey) {
      alert(t('alerts.noDepartment'));
      return;
    }

    const deptLabel = getDepartmentLabel(deptKey);

    if (!window.confirm(t('alerts.applyTemplateConfirm', { department: deptLabel }))) {
      return;
    }

    try {
      const templatePath = `companies/${companyUID}/onboarding_templates/${deptKey}`;
      const templateSnap = await get(ref(database, templatePath));

      if (!templateSnap.exists()) {
        alert(t('alerts.noTemplateFound', { department: deptLabel }));
        return;
      }

      const templateTasks = templateSnap.val();
      const updates = {};
      const taskReminders = []; // Store tasks to schedule reminders

      // Generate new task IDs and adjust due dates based on start date
      const startDate = new Date(selectedHire.startDate);

      Object.entries(templateTasks).forEach(([category, tasks]) => {
        Object.entries(tasks).forEach(([_, task]) => {
          const newTaskId = `task_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

          // Adjust due date relative to start date
          let newDueDate = task.dueDate;
          if (task.dueDate) {
            const originalDue = new Date(task.dueDate);
            const daysDiff = Math.floor((originalDue - startDate) / (1000 * 60 * 60 * 24));
            const adjustedDate = new Date(startDate);
            adjustedDate.setDate(adjustedDate.getDate() + daysDiff);
            newDueDate = adjustedDate.toISOString().split('T')[0];
          }

          updates[`companies/${companyUID}/onboarding/${selectedHire.id}/tasks/${category}/${newTaskId}`] = {
            ...task,
            dueDate: newDueDate,
            status: 'not_started',
            createdAt: Date.now()
          };

          // Add to reminders list if there's an assignee and due date
          if (task.assignee && newDueDate) {
            taskReminders.push({
              taskId: newTaskId,
              category,
              title: task.title,
              description: task.description || '',
              dueDate: newDueDate,
              assignee: task.assignee
            });
          }
        });
      });

      await update(ref(database), updates);

      // Email reminders are handled automatically by daily digest
      // No need to manually schedule reminders for template tasks

      // Refresh selected hire to show new tasks
      const hireRef = ref(database, `companies/${companyUID}/onboarding/${selectedHire.id}`);
      const hireSnap = await get(hireRef);
      if (hireSnap.exists()) {
        setSelectedHire({
          id: selectedHire.id,
          ...hireSnap.val(),
          department: normalizeDepartment(hireSnap.val().department)
        });
      }

      alert(t('alerts.applyTemplateSuccess'));
    } catch (error) {
      console.error('Error applying department template:', error);
      alert(t('alerts.applyTemplateFailed'));
    }
  };

  const normalizeAssigneeGroupKey = (value) => {
    const key = normalizeText(value).replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    if (!key) return '';
    if (key === 'newhire' || key === 'candidate' || key === 'employee') return 'new_hire';
    if (key === 'human_resources' || key === 'people_ops' || key === 'people_operations') return 'hr';
    if (key === 'information_technology' || key === 'technology' || key === 'tech') return 'it';
    if (['automation', 'automations', 'system', 'auto', 'system_auto'].includes(key)) return 'automations';
    if (['new_hire', 'hr', 'manager', 'it', 'legal', 'finance', 'automations'].includes(key)) return key;
    return key;
  };

  const getTaskAssigneeEmails = (task = {}) => {
    const rawAssignees = [
      ...(Array.isArray(task.assignees) ? task.assignees : []),
      task.assignee,
      task.assigneeEmail
    ];
    const seen = new Set();
    return rawAssignees
      .map(value => String(value || '').trim())
      .filter(value => value && value.includes('@'))
      .filter(value => {
        const key = normalizeText(value);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  };

  const getHireAutomationType = (task = {}) => {
    if (task.automationType === 'document') return 'document_send';
    if (task.automationType === 'form') return 'form_collect';
    if (task.automationType === 'meeting') return 'meeting_schedule';
    if (task.automationType) return task.automationType;
    if (task.type === 'documentRequest' || task.portalUploadRequired || task.documentUploadLink) return 'document_request';
    if (task.formConfig || task.formLink) return 'form_collect';
    if (task.meetingConfig) return 'meeting_schedule';
    if ((task.type === 'automated' || task.type === 'document') && Array.isArray(task.attachments) && task.attachments.length > 0) return 'document_send';
    if (task.type === 'automated') return 'scheduled_email';
    return '';
  };

  const inferHireTaskGroupKey = (task = {}) => {
    const automationType = getHireAutomationType(task);
    if (NEW_HIRE_AUTOMATION_TYPES.has(automationType)) return 'new_hire';
    if (PURE_AUTOMATION_TYPES.has(automationType)) return 'automations';
    const explicitGroup = normalizeAssigneeGroupKey(task.assigneeCategory || task.assignmentGroup || task.assigneeRole);
    if (explicitGroup) return explicitGroup;

    if (task.assignee === 'System' || task.assigneeName === 'System (Auto)') return 'automations';

    const assigneeEmails = getTaskAssigneeEmails(task);
    if (assigneeEmails.some(email => normalizeText(email) === normalizeText(selectedHire?.email))) return 'new_hire';
    if (assigneeEmails.some(email => normalizeText(email) === normalizeText(selectedHire?.manager))) return 'manager';

    for (const email of assigneeEmails) {
      const person = getPersonByEmail(email);
      if (person && looksLikeLegal(person)) return 'legal';
      if (person && looksLikeFinance(person)) return 'finance';
      if (person && looksLikeIT(person)) return 'it';
      if (person && looksLikeHR(person)) return 'hr';
    }

    return normalizeAssigneeGroupKey(inferAssigneeCategory(task)) || 'manager';
  };

  const getHireTaskTypeLabel = (task = {}) => {
    const automationType = getHireAutomationType(task);
    if (automationType === 'document_request') {
      return t('templatesEditor.taskTypes.document_request', 'Document Request');
    }
    if (automationType === 'document_send') {
      return t('templatesEditor.taskTypes.document_send', 'Document Send');
    }
    if (automationType === 'form_collect') {
      return t('templatesEditor.taskTypes.form_collect', 'Form Collection');
    }
    if (automationType === 'meeting_schedule') {
      return t('templatesEditor.taskTypes.meeting_schedule', 'Meeting Schedule');
    }
    if (automationType === 'scheduled_email') {
      return t('templatesEditor.taskTypes.scheduled_email', 'Email Automation');
    }
    if (task.type === 'automated' || automationType) {
      return t('workflow.automatedTask', 'Automated Task');
    }
    return t('templatesEditor.taskTypes.human', 'Manual Task');
  };

  const getHireTaskTimingLabel = (task = {}) => {
    if (task.dueDate) return formatDate(task.dueDate);
    if (task.executionMode === 'immediate') return t('workflow.immediate', 'Immediate');
    return t('workflow.noDate', 'No date');
  };

  const getHireTaskUpdatedLabel = (task = {}) => {
    const value = task.updatedAt || task.completedAt || task.createdAt;
    if (!value) return t('templatesEditor.notUpdated', 'Not saved yet');
    const date = typeof value === 'number'
      ? new Date(value > 9999999999 ? value : value * 1000)
      : new Date(value);
    if (Number.isNaN(date.getTime())) return t('templatesEditor.notUpdated', 'Not saved yet');
    return date.toLocaleDateString(i18n.language === 'ar' ? 'ar-EG' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getHireTaskAssigneeLabel = (task = {}) => {
    const automationType = getHireAutomationType(task);
    if (NEW_HIRE_AUTOMATION_TYPES.has(automationType)) {
      return t('templatesEditor.groupShort.new_hire', 'New Hire');
    }
    if (PURE_AUTOMATION_TYPES.has(automationType)) {
      return t('workflow.systemAuto', 'System (Auto)');
    }

    const assigneeNames = new Set();
    if (task.assigneeName && task.assigneeName !== 'System (Auto)') assigneeNames.add(task.assigneeName);
    getTaskAssigneeEmails(task).forEach(email => {
      if (normalizeText(email) === normalizeText(selectedHire?.email)) {
        assigneeNames.add(selectedHire?.name || email);
        return;
      }
      if (normalizeText(email) === normalizeText(selectedHire?.manager)) {
        assigneeNames.add(getPersonByEmail(email)?.name || t('workflow.manager', 'Manager'));
        return;
      }
      assigneeNames.add(getPersonByEmail(email)?.name || email);
    });

    if (assigneeNames.size > 0) return Array.from(assigneeNames).join(', ');
    if (task.assignee === 'System' || task.assigneeName === 'System (Auto)' || task.type === 'automated' || automationType) {
      return t('workflow.systemAuto', 'System (Auto)');
    }

    const groupKey = normalizeAssigneeGroupKey(task.assigneeCategory);
    if (groupKey) return t(`templatesEditor.groupShort.${groupKey}`, groupKey.toUpperCase());
    return t('tasks.assigneePlaceholder', 'Unassigned');
  };

  const duplicateHireCategoryTask = async (stage, task) => {
    if (tutorialActiveRef.current || !selectedHire?.id || !task?.id) return;
    if (!canManageOnboarding) {
      showOnboardingPermissionAlert();
      return;
    }

    try {
      const sourceIsWorkflowTask = Boolean(selectedHire?.tasks?.[stage]?.workflow?.tasks?.[task.id]);
      const tasksPath = sourceIsWorkflowTask
        ? `companies/${companyUID}/onboarding/${selectedHire.id}/tasks/${stage}/workflow/tasks`
        : `companies/${companyUID}/onboarding/${selectedHire.id}/tasks/${stage}`;
      const taskRef = push(ref(database, tasksPath));
      const duplicatedTask = {
        ...task,
        id: taskRef.key,
        title: t('templatesEditor.copyOfTask', { title: task.title || t('templatesEditor.untitledTask', 'Untitled Task') }),
        status: 'not_started',
        createdAt: Date.now(),
        updatedAt: Date.now()
      };
      delete duplicatedTask.__isWorkflowTask;
      delete duplicatedTask.stage;

      await set(taskRef, duplicatedTask);
    } catch (error) {
      console.error('Error duplicating task:', error);
      alert(t('alerts.errorCreatingTask'));
    }
  };

  const hireDepartmentFilters = useMemo(() => {
    const counts = new Map();

    newHires.forEach((hire) => {
      const department = normalizeDepartment(hire?.department);
      if (!department) return;
      counts.set(department, (counts.get(department) || 0) + 1);
    });

    return Array.from(counts.entries())
      .map(([department, count]) => ({
        department,
        label: getDepartmentLabel(department),
        count
      }))
      .sort((a, b) => a.label.localeCompare(b.label, i18n.language || undefined));
  }, [newHires, departmentOptionsList, i18n.language]);

  useEffect(() => {
    if (
      activeHireDepartmentFilter !== 'all' &&
      !hireDepartmentFilters.some(filter => filter.department === activeHireDepartmentFilter)
    ) {
      setActiveHireDepartmentFilter('all');
    }
  }, [activeHireDepartmentFilter, hireDepartmentFilters]);

  const visibleNewHires = activeHireDepartmentFilter === 'all'
    ? newHires
    : newHires.filter(hire => normalizeDepartment(hire?.department) === activeHireDepartmentFilter);

  const myTasksTodayByHire = useMemo(() => {
    const groups = new Map();

    myTasksToday.forEach((task) => {
      const hireName = task?.hireName || t('labels.unspecified');
      if (!groups.has(hireName)) {
        groups.set(hireName, []);
      }
      groups.get(hireName).push(task);
    });

    return Array.from(groups.entries()).map(([hireName, tasks]) => ({
      hireName,
      tasks
    }));
  }, [myTasksToday, t]);

  const overdueTaskAlerts = useMemo(() => {
    const today = getTodayLocalDate();
    const alerts = [];

    newHires.forEach((hire) => {
      forEachHireTask(hire, (task, category, taskId) => {
        const dueDate = getTaskLocalDate(task?.dueDate);
        if (!task || isTaskComplete(task) || !dueDate || dueDate >= today) return;

	        const assignees = Array.isArray(task.assignees)
	          ? task.assignees
	          : (task.assignee ? [task.assignee] : []);
	        const assigneeLabel = task.assigneeName || assignees
	          .map(assignee => {
	            if (!assignee) return '';
	            if (typeof assignee === 'object') {
	              return assignee.name || assignee.displayName || assignee.email || assignee.uid || assignee.id || '';
	            }
	            return assignee;
	          })
	          .filter(Boolean)
	          .join(', ');

	        alerts.push({
	          ...task,
	          hireId: hire.id,
	          hireName: hire.name,
	          category,
	          taskId,
	          assigneeLabel: assigneeLabel || t('tasks.assigneePlaceholder')
	        });
      });
    });

    return alerts.sort((a, b) => {
      const hireCompare = String(a.hireName || '').localeCompare(String(b.hireName || ''), i18n.language || undefined);
      if (hireCompare !== 0) return hireCompare;
      return String(a.dueDate || '').localeCompare(String(b.dueDate || ''));
    });
  }, [newHires, i18n.language, t]);

  const overdueAlertsByHire = useMemo(() => {
    const groups = new Map();

    overdueTaskAlerts.forEach((task) => {
      const hireName = task?.hireName || t('labels.unspecified');
      if (!groups.has(hireName)) {
        groups.set(hireName, []);
      }
      groups.get(hireName).push(task);
    });

    return Array.from(groups.entries()).map(([hireName, tasks]) => ({
      hireName,
      tasks
    }));
  }, [overdueTaskAlerts, t]);

  const selectedHireProgress = selectedHire ? calculateProgress(selectedHire) : 0;

  const waitForElement = (selector, timeout = 8000) => {
    return new Promise((resolve, reject) => {
      const start = Date.now();
      const observer = new MutationObserver(() => {
        const el = document.querySelector(selector);
        if (el) {
          observer.disconnect();
          resolve(el);
        } else if (Date.now() - start > timeout) {
          observer.disconnect();
          reject(new Error(`Timeout waiting for ${selector}`));
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
      const el = document.querySelector(selector);
      if (el) {
        observer.disconnect();
        resolve(el);
      }
    });
  };

  const waitForElementRemoval = (selector, timeout = 5000) => {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      const check = () => {
        const element = document.querySelector(selector);
        if (!element) {
          resolve();
        } else if (Date.now() - startTime > timeout) {
          reject(new Error(`Element ${selector} still present after ${timeout}ms`));
        } else {
          setTimeout(check, 50);
        }
      };
      check();
    });
  };

  const toggleGlow = (selectors, enable = true) => {
    const list = Array.isArray(selectors) ? selectors : [selectors];
    list.forEach(selector => {
      if (!selector) return;
      document.querySelectorAll(selector).forEach(node => {
        node.classList.toggle('tutorial-glow', enable);
      });
    });
  };

  const clearTutorialHighlights = () => {
    document.querySelectorAll('.tutorial-glow').forEach(node => {
      node.classList.remove('tutorial-glow');
    });
  };

  const focusElementSmoothly = (element) => {
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    }
  };

  const createOnboardingTutorialDataset = (assigneeEmail) => {
    const isArabic = isRTL || language === 'ar';
    const trTutorial = (en, ar) => (isArabic ? ar : en);
    const now = Date.now();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dayMs = 24 * 60 * 60 * 1000;
    const format = (date) => {
      const clone = new Date(date);
      clone.setHours(0, 0, 0, 0);
      return clone.toISOString().split('T')[0];
    };

    const assignedEmail = assigneeEmail || 'you@jobseeqr.com';
    const todayStr = format(today);
    const startInThreeDays = format(new Date(today.getTime() + 3 * dayMs));
    const startInTenDays = format(new Date(today.getTime() + 10 * dayMs));

    const hireAlexTasks = {
      'Before Day One': {
        'task_welcome': {
          title: trTutorial('Send welcome email', 'إرسال بريد ترحيبي'),
          description: trTutorial('Share welcome email with first-day agenda and portal link.', 'أرسل بريدًا ترحيبيًا مع جدول اليوم الأول ورابط البوابة.'),
          dueDate: todayStr,
          assignee: assignedEmail,
          status: 'not_started',
          type: 'standard',
          createdAt: now - dayMs
        },
        'task_equipment': {
          title: trTutorial('Prepare design workstation', 'تجهيز محطة عمل التصميم'),
          description: trTutorial('MacBook Pro, Figma license, and prototyping kit.', 'ماك بوك برو، ترخيص Figma، وأدوات النمذجة.'),
          dueDate: format(new Date(today.getTime() + dayMs)),
          assignee: 'ops@jobseeqr.com',
          status: 'in_progress',
          type: 'standard',
          createdAt: now - dayMs
        }
      },
      'Day One': {
        'task_day1_it': {
          title: trTutorial('IT onboarding session', 'جلسة تعريف مع تقنية المعلومات'),
          description: trTutorial('30-minute walkthrough with IT to confirm tool access.', 'جولة لمدة 30 دقيقة مع فريق تقنية المعلومات للتأكد من صلاحيات الأدوات.'),
          dueDate: startInThreeDays,
          assignee: 'it@jobseeqr.com',
          status: 'not_started',
          type: 'standard',
          createdAt: now
        },
        'task_team_lunch': {
          title: trTutorial('Team welcome lunch', 'غداء ترحيبي مع الفريق'),
          description: trTutorial('Introduce Alex to product squad over lunch.', 'تعريف أليكس بفريق المنتج خلال الغداء.'),
          dueDate: startInThreeDays,
          assignee: 'jordan.lee@jobseeqr.com',
          status: 'not_started',
          type: 'standard',
          createdAt: now
        }
      },
      'First Week': {
        'task_research': {
          title: trTutorial('Walk through design system', 'جولة في نظام التصميم'),
          description: trTutorial('Buddy leads onboarding to component library and standards.', 'الزميل يعرّفها على مكتبة المكونات والمعايير.'),
          dueDate: format(new Date(today.getTime() + 6 * dayMs)),
          assignee: 'robin.chen@jobseeqr.com',
          status: 'not_started',
          type: 'standard',
          createdAt: now
        }
      },
      'First Month': {
        'task_project': {
          title: trTutorial('Ship first feature prototype', 'تسليم النموذج الأولي لأول ميزة'),
          description: trTutorial('Collaborate with PM on the new onboarding checklist experience.', 'التعاون مع مدير المنتج على تجربة قائمة الإعداد الجديدة.'),
          dueDate: format(new Date(today.getTime() + 24 * dayMs)),
          assignee: assignedEmail,
          status: 'in_progress',
          type: 'standard',
          createdAt: now
        }
      },
      '90-Day Review': {
        'task_review': {
          title: trTutorial('90-day performance review', 'مراجعة أداء بعد 90 يومًا'),
          description: trTutorial('Collect feedback from stakeholders before review meeting.', 'جمع ملاحظات أصحاب المصلحة قبل اجتماع التقييم.'),
          dueDate: format(new Date(today.getTime() + 95 * dayMs)),
          assignee: 'people@jobseeqr.com',
          status: 'not_started',
          type: 'standard',
          createdAt: now
        }
      }
    };

    const hireMayaTasks = {
      'Before Day One': {
        'task_offer_followup': {
          title: trTutorial('Confirm relocation details', 'تأكيد تفاصيل الانتقال'),
          description: trTutorial('Double-check relocation stipend and housing support.', 'التأكد من بدل الانتقال ودعم السكن.'),
          dueDate: format(new Date(today.getTime() + 2 * dayMs)),
          assignee: 'hr@jobseeqr.com',
          status: 'not_started',
          type: 'standard',
          createdAt: now
        }
      },
      'Day One': {},
      'First Week': {},
      'First Month': {},
      '90-Day Review': {}
    };

    const hires = [
      {
        id: '_DEMO_HIRE_001',
        name: trTutorial('Alex Rivera', 'أليكس ريفيرا'),
        email: 'alex.rivera@example.com',
        role: trTutorial('Product Designer', 'مصمم منتجات'),
        department: trTutorial('Design', 'التصميم'),
        startDate: startInThreeDays,
        manager: trTutorial('Jordan Lee', 'جوردان لي'),
        buddyMentor: trTutorial('Robin Chen', 'روبن تشين'),
        createdAt: now - 3 * dayMs,
        tasks: hireAlexTasks
      },
      {
        id: '_DEMO_HIRE_002',
        name: trTutorial('Maya Patel', 'مايا باتيل'),
        email: 'maya.patel@example.com',
        role: trTutorial('Customer Success Manager', 'مدير نجاح العملاء'),
        department: trTutorial('Success', 'نجاح العملاء'),
        startDate: startInTenDays,
        manager: trTutorial('Priya Malhotra', 'بريا مالهوترا'),
        buddyMentor: trTutorial('Lucas Wong', 'لوكاس وونغ'),
        createdAt: now - 5 * dayMs,
        tasks: hireMayaTasks
      }
    ];

    const tasksToday = [];
    hires.forEach(hire => {
      if (!hire.tasks) return;
      Object.entries(hire.tasks).forEach(([category, tasks]) => {
        Object.entries(tasks).forEach(([taskId, task]) => {
          if (task.assignee === assignedEmail && task.dueDate === todayStr && task.status !== 'completed') {
            tasksToday.push({
              ...task,
              hireId: hire.id,
              hireName: hire.name,
              category,
              taskId
            });
          }
        });
      });
    });

    return {
      hires: cloneData(hires),
      tasksToday: cloneData(tasksToday),
      primaryHireId: hires[0].id
    };
  };

  const ensureTutorialDataset = (force = false) => {
    const hasDataset = Array.isArray(tutorialSampleHiresRef.current) && tutorialSampleHiresRef.current.length > 0;
    if (!force && hasDataset) {
      return tutorialSampleHiresRef.current;
    }

    const dataset = createOnboardingTutorialDataset(currentUser?.email || '');
    tutorialSampleHiresRef.current = cloneData(dataset.hires);
    tutorialHireIdRef.current = dataset.primaryHireId;
    setNewHires(cloneData(dataset.hires));
    setMyTasksToday(cloneData(dataset.tasksToday));
    return tutorialSampleHiresRef.current;
  };

  const cleanupOnboardingTutorial = () => {
    clearTutorialHighlights();
    const backup = tutorialBackupRef.current;
    tutorialBackupRef.current = null;
    tutorialSampleHiresRef.current = [];
    tutorialHireIdRef.current = '';
    tutorialActiveRef.current = false;
    setTutorialActive(false);
    if (backup) {
      setNewHires(cloneData(backup.newHires));
      setSelectedHire(backup.selectedHire ? cloneData(backup.selectedHire) : null);
      setMyTasksToday(cloneData(backup.myTasksToday));
      setExpandedCategories(cloneData(backup.expandedCategories));
      setLoading(backup.loading);
    }
  };

  const startOnboardingTutorial = () => {
    if (tutorialActive) {
      return;
    }

    tutorialBackupRef.current = {
      newHires: cloneData(newHires),
      selectedHire: selectedHire ? cloneData(selectedHire) : null,
      myTasksToday: cloneData(myTasksToday),
      expandedCategories: cloneData(expandedCategories),
      loading
    };

    tutorialActiveRef.current = true;
    ensureTutorialDataset(true);
    const primaryHireId = tutorialHireIdRef.current;

    setTutorialActive(true);
    setLoading(false);
    selectHireForView(null);
    setShowAddHireModal(false);
    setShowAddTaskModal(false);
    setShowEmailPreview(false);
    setShowPlaceholderModal(false);
    setExpandedCategories({
      'Before Day One': true,
      'Day One': true,
      'First Week': true,
      'First Month': true,
      '90-Day Review': true
    });
    clearTutorialHighlights();



    const openTutorialHire = async () => {
      const hires = ensureTutorialDataset();
      const hire = hires.find(h => h.id === tutorialHireIdRef.current);
      if (!hire) return null;

      // Expand all categories
      setExpandedCategories({
        'Before Day One': true,
        'Day One': true,
        'First Week': true,
        'First Month': true,
        '90-Day Review': true
      });

      // Force React to commit the selection synchronously
      try {
        flushSync(() => {
          selectHireForView(hire, { trackTutorialHire: true });
        });
      } catch {
        selectHireForView(hire, { trackTutorialHire: true });
      }

      // Wait until the hire overview is actually visible in DOM
      await waitForElement('[data-tutorial="hire-overview"]', 8000);
      return hire;
    };

    const doubleRAF = () =>
      new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));


    const launchTutorial = () => {
      let driverInstance;

      const tutorialCopy = {
        intro: { title: t('tutorial.steps.intro.title'), description: t('tutorial.steps.intro.description') },
        addHire: { title: t('tutorial.steps.addHire.title'), description: t('tutorial.steps.addHire.description') },
        hireCards: { title: t('tutorial.steps.hireCards.title'), description: t('tutorial.steps.hireCards.description') },
        opening: { title: t('tutorial.steps.opening.title'), description: t('tutorial.steps.opening.description') },
        plan: { title: t('tutorial.steps.plan.title'), description: t('tutorial.steps.plan.description') },
        categories: { title: t('tutorial.steps.categories.title'), description: t('tutorial.steps.categories.description') },
        templates: { title: t('tutorial.steps.templates.title'), description: t('tutorial.steps.templates.description') },
        returning: { title: t('tutorial.steps.returning.title'), description: t('tutorial.steps.returning.description') },
        tasksToday: { title: t('tutorial.steps.tasksToday.title'), description: t('tutorial.steps.tasksToday.description') },
        complete: { title: t('tutorial.steps.complete.title'), description: t('tutorial.steps.complete.description') }
      };

      const steps = [
        {
          popover: {
            title: tutorialCopy.intro.title,
            description: tutorialCopy.intro.description,
          }
        },
        {
          element: () => {
            const element = document.querySelector('[data-tutorial="add-hire"]');
            toggleGlow('[data-tutorial="add-hire"]', true);
            focusElementSmoothly(element);
            return element;
          },
          popover: {
            title: tutorialCopy.addHire.title,
            description: tutorialCopy.addHire.description,
            side: 'left',
            align: 'start'
          },
          onNextClick: () => toggleGlow('[data-tutorial="add-hire"]', false),
          onPrevClick: () => toggleGlow('[data-tutorial="add-hire"]', false)
        },
        {
          element: () => {
            const selector = `[data-hire-id="${tutorialHireIdRef.current}"]`;
            const element = document.querySelector(selector);
            toggleGlow(selector, true);
            focusElementSmoothly(element);
            return element;
          },
          popover: {
            title: tutorialCopy.hireCards.title,
            description: tutorialCopy.hireCards.description,
            side: 'right',
            align: 'start'
          },
          onNextClick: () => {
            const selector = `[data-hire-id="${tutorialHireIdRef.current}"]`;
            toggleGlow(selector, false);
            // Move off the anchored element BEFORE changing the UI.
            driverInstance.moveNext();
            return false;
          },
          onPrevClick: () => toggleGlow(`[data-hire-id="${tutorialHireIdRef.current}"]`, false)
        },
        {
          // Bridge step with no element — allows DOM to change safely.
          popover: {
            title: tutorialCopy.opening.title,
            description: tutorialCopy.opening.description,
          },
          onHighlighted: async () => {
            // Open Alex's detail while no element is highlighted by Driver.
            await openTutorialHire();

            // Give the browser a full paint cycle so Driver can position correctly.
            await doubleRAF();

            const el = document.querySelector('[data-tutorial="hire-overview"]');
            if (el) focusElementSmoothly(el);

            // Proceed once the target exists.
            driverInstance.moveNext();
          },
        },
        {
          element: () => {
            const element = document.querySelector('[data-tutorial="hire-overview"]');
            toggleGlow('[data-tutorial="hire-overview"]', true);
            focusElementSmoothly(element);
            return element;
          },
          popover: {
            title: tutorialCopy.plan.title,
            description: tutorialCopy.plan.description,
            side: 'top',
            align: 'center'
          },
          onNextClick: () => toggleGlow('[data-tutorial="hire-overview"]', false),
          onPrevClick: () => {
            toggleGlow('[data-tutorial="hire-overview"]', false);
            try {
              flushSync(() => selectHireForView(null));
            } catch {
              selectHireForView(null);
            }
            waitForElement(`[data-hire-id="${tutorialHireIdRef.current}"]`)
              .then(() => driverInstance.movePrevious())
              .catch(() => driverInstance.movePrevious());
            return false;
          }
        },
        {
          element: () => {
            const element = document.querySelector('[data-tutorial="hire-tasks"]');
            toggleGlow('[data-tutorial="hire-tasks"]', true);
            focusElementSmoothly(element);
            return element;
          },
          popover: {
            title: tutorialCopy.categories.title,
            description: tutorialCopy.categories.description,
            side: 'right',
            align: 'start'
          },
          onNextClick: () => toggleGlow('[data-tutorial="hire-tasks"]', false),
          onPrevClick: () => toggleGlow('[data-tutorial="hire-tasks"]', false)
        },
        {
          element: () => {
            const element = document.querySelector('[data-tutorial="template-actions"]');
            toggleGlow('[data-tutorial="template-actions"]', true);
            focusElementSmoothly(element);
            return element;
          },
          popover: {
            title: tutorialCopy.templates.title,
            description: tutorialCopy.templates.description,
            side: 'top',
            align: 'end'
          },
          onNextClick: () => {
            // Just advance; the actual view switch happens in the next (bridge) step.
            toggleGlow('[data-tutorial="template-actions"]', false);
            driverInstance.moveNext();
            return false;
          },
          onPrevClick: () => toggleGlow('[data-tutorial="template-actions"]', false)
        },
        {
          // BRIDGE STEP: go back to the main onboarding overview BEFORE we anchor "Your Tasks Today"
          popover: {
            title: tutorialCopy.returning.title,
            description: tutorialCopy.returning.description,
          },
          onHighlighted: async () => {
            try {
              flushSync(() => selectHireForView(null));
            } catch {
              selectHireForView(null);
            }

            // Wait for the overview UI to exist
            await waitForElement('[data-tutorial="tasks-today"]', 8000).catch(() => { });

            // Ensure the detail header is gone (if it was open)
            await waitForElementRemoval('[data-tutorial="hire-overview"]', 1500).catch(() => { });

            // Two animation frames to give the layout time to settle for Driver.js positioning
            await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

            driverInstance.moveNext();
          }
        },
        {
          element: () => {
            const element = document.querySelector('[data-tutorial="tasks-today"]');
            toggleGlow('[data-tutorial="tasks-today"]', true);
            focusElementSmoothly(element);
            return element;
          },
          popover: {
            title: tutorialCopy.tasksToday.title,
            description: tutorialCopy.tasksToday.description,
            side: 'left',
            align: 'center'
          },
          onNextClick: () => toggleGlow('[data-tutorial="tasks-today"]', false),
          onPrevClick: () => {
            toggleGlow('[data-tutorial="tasks-today"]', false);
            openTutorialHire();
            waitForElement('[data-tutorial="hire-overview"]')
              .then(() => driverInstance.movePrevious())
              .catch(() => driverInstance.movePrevious());
            return false;
          }
        },
        {
          popover: {
            title: tutorialCopy.complete.title,
            description: tutorialCopy.complete.description
          },
          onPrevClick: () => {
            toggleGlow('[data-tutorial="tasks-today"]', false);
            openTutorialHire();
            waitForElement('[data-tutorial="hire-overview"]')
              .then(() => driverInstance.movePrevious())
              .catch(() => driverInstance.movePrevious());
            return false;
          }
        }
      ];

      driverInstance = driver({
        showProgress: true,
        steps,
        onDestroyStarted: () => {
          cleanupOnboardingTutorial();
          if (companyUID) {
            set(ref(database, `companies/${companyUID}/tutorialProgress/onboarding`), true).catch(error => {
              console.error('Error saving onboarding tutorial progress:', error);
            });
            set(ref(database, `companies/${companyUID}/hasCompletedTutorial`), true).catch(error => {
              console.error('Error flagging tutorial completion:', error);
            });
          }
          setTimeout(() => window.location.reload(), 150);
        },
        onDestroyed: () => { }
      });

      driverInstance.drive();
    };

    waitForElement(`[data-hire-id="${primaryHireId}"]`, 4000)
      .then(() => launchTutorial())
      .catch(() => launchTutorial());
  };

  // Auto-start tutorial from URL parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('startTutorial') === 'true' && companyUID) {
      setTimeout(() => {
        startOnboardingTutorial();
      }, 1500);
      // Clear URL parameter
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [companyUID]);

  // Listen for tutorial trigger from ProductProtectedRoute
  useEffect(() => {
    const handleTutorialFromOverlay = (event) => {
      console.log('[Onboarding] Received startTutorialFromOverlay event', event.detail);
      const { onTutorialComplete } = event.detail;

      // Start the tutorial
      startOnboardingTutorial();

      // Note: The tutorial completion will be handled by the driver's onDestroyed callback
      // ProductProtectedRoute will check access after overlay is dismissed
    };

    window.addEventListener('startTutorialFromOverlay', handleTutorialFromOverlay);
    return () => {
      window.removeEventListener('startTutorialFromOverlay', handleTutorialFromOverlay);
    };
  }, []);

  const sidebarWidth = sidebarCollapsed ? '60px' : '240px';
  const selectedHireStageProgress = useMemo(() => {
    const progress = {};
    STAGES.forEach(stage => {
      const tasks = collectHireStageTaskEntries(selectedHire, stage);
      progress[stage] = {
        total: tasks.length,
        completed: tasks.filter(task => task.status === 'completed').length
      };
    });
    return progress;
  }, [selectedHire]);

  const categoryViewGroupDefinitions = [
    { key: 'new_hire', label: t('templatesEditor.groups.new_hire', 'Tasks for New Hire') },
    { key: 'hr', label: t('templatesEditor.groups.hr', 'Tasks for HR') },
    { key: 'manager', label: t('templatesEditor.groups.manager', 'Tasks for Manager') },
    { key: 'it', label: t('templatesEditor.groups.it', 'Tasks for IT') },
    { key: 'legal', label: t('templatesEditor.groups.legal', 'Tasks for Legal') },
    { key: 'finance', label: t('templatesEditor.groups.finance', 'Tasks for Finance') },
    { key: 'automations', label: t('templatesEditor.groups.automations', 'Automations') }
  ];
  const activeStageTaskEntries = collectHireStageTaskEntries(selectedHire, activeStage)
    .sort((a, b) => {
      const rawDateA = a.dueDate ? new Date(a.dueDate).getTime() : Number.NaN;
      const rawDateB = b.dueDate ? new Date(b.dueDate).getTime() : Number.NaN;
      const dateA = Number.isFinite(rawDateA) ? rawDateA : Number.MAX_SAFE_INTEGER;
      const dateB = Number.isFinite(rawDateB) ? rawDateB : Number.MAX_SAFE_INTEGER;
      if (dateA !== dateB) return dateA - dateB;
      return String(a.title || '').localeCompare(String(b.title || ''), i18n.language || undefined);
    });
  const categoryViewTaskGroupsMap = new Map(
    categoryViewGroupDefinitions.map(group => [group.key, { ...group, tasks: [] }])
  );
  activeStageTaskEntries.forEach(task => {
    const groupKey = normalizeAssigneeGroupKey(inferHireTaskGroupKey(task)) || 'manager';
    if (!categoryViewTaskGroupsMap.has(groupKey)) {
      categoryViewTaskGroupsMap.set(groupKey, {
        key: groupKey,
        label: t('templatesEditor.groups.unknown', { group: groupKey.replace(/_/g, ' ') }),
        tasks: []
      });
    }
    categoryViewTaskGroupsMap.get(groupKey).tasks.push(task);
  });
  const categoryViewTaskGroups = Array.from(categoryViewTaskGroupsMap.values());
  const handleCategoryTaskMenuToggle = (rowMenuKey, event) => {
    event.stopPropagation();
    if (categoryTaskMenu === rowMenuKey) {
      setCategoryTaskMenu(null);
      setCategoryTaskMenuPosition(null);
      return;
    }

    setCategoryTaskMenu(rowMenuKey);
    setCategoryTaskMenuPosition(getFixedInlineMenuPosition(event.currentTarget));
  };

  return (
    <div style={{ height: '100vh' }}>
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />

      <div
        className={`onboarding-body ${isRTL ? 'rtl' : 'ltr'}`}
        style={{
          marginLeft: isRTL ? '0px' : sidebarWidth,
          marginRight: isRTL ? sidebarWidth : '0px',
          transition: 'all 250ms ease',
          width: `calc(100vw - ${sidebarWidth})`,
          boxSizing: 'border-box',
          paddingTop: selectedHire ? '60px' : '65px',
          paddingBottom: '40px',
          paddingLeft: selectedHire ? (!isRTL && sidebarCollapsed ? '100px' : '40px') : '32px',
          paddingRight: selectedHire ? (isRTL && sidebarCollapsed ? '100px' : '40px') : '32px',
          overflow: 'auto',
          height: '100vh',
          direction: isRTL ? 'rtl' : 'ltr'
        }}
      >
        <FeatureGate showBanner={false}>

          {!selectedHire ? (
            <div className="onboarding-main-layout">
              <div className="gallery-section">
              <div className="onboarding-list-header">
                <h1 className="onboarding-title">{t('pageTitle')}</h1>
	                {canManageOnboarding && (
	                  <div className="header-actions onboarding-list-actions">
	                    <button
	                      className="btn-onboarding-header"
                          data-light-control="quiet"
	                      onClick={() => setShowTemplatesModal(true)}
                      title={t('tooltips.manageTemplates', 'Manage onboarding templates')}
                    >
                      <i className="fa-solid fa-layer-group"></i>
                      {t('buttons.templates', 'Manage Templates')}
                    </button>
                    <button className="btn-add-hire" data-tutorial="add-hire" onClick={() => setShowAddHireModal(true)}>
                      <i className="fa-solid fa-circle-plus"></i>
                      {t('buttons.addNewHire')}
                    </button>
	                  </div>
	                )}
	              </div>

	              <div className="onboarding-hire-filters" aria-label={t('filters.hireFilters')}>
	                <button
	                  type="button"
	                  className={`onboarding-hire-filter ${activeHireDepartmentFilter === 'all' ? 'active' : ''}`}
	                  onClick={() => setActiveHireDepartmentFilter('all')}
	                >
	                  {t('filters.allNewHires', { count: newHires.length })}
	                </button>
	                {hireDepartmentFilters.map(filter => (
	                  <button
	                    type="button"
	                    key={filter.department}
	                    className={`onboarding-hire-filter ${activeHireDepartmentFilter === filter.department ? 'active' : ''}`}
	                    onClick={() => setActiveHireDepartmentFilter(filter.department)}
	                  >
	                    {uiText(filter.label)} ({filter.count})
	                  </button>
	                ))}
	              </div>

                  {loading ? (
                    <div className="loading-state">
                      <i className="fa-solid fa-spinner fa-spin"></i>
                      <p>{t('states.loading')}</p>
                    </div>
                  ) : newHires.length === 0 ? (
                    <div className="empty-state">
                      <i className="fa-solid fa-user-plus"></i>
                      <p>{t('states.noHires')}</p>
                      {canManageOnboarding && (
                        <button className="btn-primary" onClick={() => setShowAddHireModal(true)}>
                          {t('buttons.addFirstHire')}
                        </button>
                      )}
                    </div>
                  ) : (
	                    <div className="hires-gallery">
	                      {visibleNewHires.map(hire => {
	                        const currentStepKey = getCurrentStepKey(hire);
	                        const currentStepLabel = getCategoryLabel(currentStepKey);
	                        const overdue = getOverdueTasks(hire);
	                        const dueToday = getTasksDueToday(hire);
	                        const stageCompletion = getTaskCompletionForStage(hire, currentStepKey);
	                        const nextTaskDueDate = formatCardDueDate(getNextTaskDueDate(hire));
	                        const hasDueAlerts = overdue > 0 || dueToday > 0;

	                        return (
	                          <div
                            key={hire.id}
                            className="hire-card"
                            data-hire-id={hire.id}
	                            role="link"
                            tabIndex={0}
                            onKeyDown={event => {
                              if (event.key === 'Enter') selectHireForView(hire, { trackTutorialHire: tutorialActive });
                            }}
	                            onClick={() => selectHireForView(hire, { trackTutorialHire: tutorialActive })}
	                          >
	                            <div className="hire-info">
	                              <h3 className="hire-card-name">{hire.name}</h3>
	                              <p className="hire-card-role" title={hire.role || t('labels.unspecified')}>{hire.role || t('labels.unspecified')}</p>

	                              <div className="hire-card-stage-row">
	                                <i className="fa-solid fa-hourglass-half"></i>
	                                <span className="hire-card-stage">{currentStepLabel || getCategoryLabel('Not Started')}</span>
	                              </div>
                                  <p className="hire-stage-completion">{t('labels.stageCompletion', { completed: stageCompletion.completed, total: stageCompletion.total })}</p>
                                  <progress className="hire-stage-progress" value={stageCompletion.completed} max={stageCompletion.total || 1} aria-label={t('labels.stageProgress')} />
	                            </div>

	                            <div className="hire-card-status">
	                              {hasDueAlerts ? (
	                                <div className="hire-card-alerts">
	                                  {overdue > 0 && (
	                                    <span className="hire-alert-pill hire-alert-overdue">
	                                      {t('labels.tasksOverdue', { count: overdue })}
	                                    </span>
	                                  )}
	                                  {dueToday > 0 && (
	                                    <span className="hire-alert-pill hire-alert-due-today">
	                                      {t('labels.tasksDueToday', { count: dueToday })}
	                                    </span>
	                                  )}
	                                </div>
	                              ) : (
	                                <>
	                                  <div className="hire-card-on-track">
	                                    <i className="fa-solid fa-circle-check"></i>
	                                    <span>{t('labels.onTrack')}</span>
	                                  </div>
	                                  <div className="hire-card-next-due">
	                                    {t('labels.nextTaskDue', { date: nextTaskDueDate })}
	                                  </div>
	                                </>
	                              )}
	                            </div>
	                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

	                {/* Right: My Tasks Today */}
	                <div className="tasks-sidebar" data-tutorial="tasks-today">
	                  <div className="tasks-panel-tabs">
	                    <button
	                      type="button"
	                      className={`tasks-panel-tab ${activeTasksPanelTab === 'tasks' ? 'active' : ''}`}
	                      onClick={() => setActiveTasksPanelTab('tasks')}
	                    >
	                      {t('tasksPanel.tabs.tasks')}
	                    </button>
	                    <button
	                      type="button"
	                      className={`tasks-panel-tab ${activeTasksPanelTab === 'alerts' ? 'active' : ''}`}
	                      onClick={() => setActiveTasksPanelTab('alerts')}
	                    >
	                      {t('tasksPanel.tabs.alerts')} · {overdueTaskAlerts.length}
	                    </button>
	                  </div>

	                  {activeTasksPanelTab === 'tasks' ? (
	                    <>
	                      <h2 className="tasks-panel-title">{t('sections.tasksToday')}</h2>
	                      {myTasksTodayByHire.length === 0 ? (
	                        <div className="no-tasks">
	                          <i className="fa-solid fa-check-circle"></i>
	                          <p>{t('states.noTasksToday')}</p>
                              {overdueTaskAlerts.length > 0 && <button type="button" className="tasks-review-alerts" onClick={() => setActiveTasksPanelTab('alerts')}>{t('tasksPanel.reviewAlerts', { count: overdueTaskAlerts.length })}</button>}
	                        </div>
	                      ) : (
	                        <div className="tasks-panel-groups">
	                          {myTasksTodayByHire.map(group => (
	                            <div key={group.hireName} className="tasks-panel-group">
	                              <h3>{group.hireName}</h3>
	                              <div className="tasks-panel-items">
	                                {group.tasks.map(task => (
	                                  (() => {
	                                    const nextStatus = task.status === 'completed' ? 'in_progress' : 'completed';
	                                    const canToggleTask = canUpdateTaskStatus(task, nextStatus);

	                                    return (
	                                      <button
	                                        type="button"
	                                        key={`${task.hireId}_${task.category}_${task.taskId}`}
	                                        className="tasks-panel-task"
	                                        onClick={() => {
	                                          const hire = newHires.find(h => h.id === task.hireId);
	                                          if (hire) {
	                                            selectHireForView(hire);
	                                            setTaskToOpen({ stage: task.category, id: task.taskId });
	                                            if (STAGES.includes(task.category)) {
	                                              setActiveStage(task.category);
	                                            }
	                                            setExpandedCategories(prev => ({
	                                              ...prev,
	                                              [task.category]: true
	                                            }));
	                                          }
	                                        }}
	                                      >
	                                        <span
	                                          className={`tasks-panel-check ${task.status === 'completed' ? 'checked' : ''} ${canToggleTask ? '' : 'disabled'}`}
	                                          onClick={(e) => {
	                                            e.stopPropagation();
	                                            if (tutorialActive || !canToggleTask) return;
	                                            updateTaskStatus(task.hireId, task.category, task.taskId, nextStatus);
	                                          }}
	                                        >
	                                          {task.status === 'completed' && <i className="fa-solid fa-circle-check"></i>}
	                                        </span>
	                                        <span>{task.title}</span>
	                                      </button>
	                                    );
	                                  })()
	                                ))}
	                              </div>
	                            </div>
	                          ))}
	                        </div>
	                      )}
	                    </>
	                  ) : (
	                    <>
	                      <h2 className="tasks-panel-title">{t('tasksPanel.alertsTitle')}</h2>
	                      {overdueAlertsByHire.length === 0 ? (
	                        <div className="no-tasks">
	                          <i className="fa-solid fa-check-circle"></i>
	                          <p>{t('tasksPanel.noAlerts')}</p>
	                        </div>
	                      ) : (
	                        <div className="tasks-panel-groups">
	                          {overdueAlertsByHire.map(group => (
	                            <div key={group.hireName} className="tasks-panel-group">
	                              <h3>{group.hireName}</h3>
	                              <div className="tasks-panel-items">
	                                {group.tasks.map(task => (
	                                  <button
	                                    type="button"
	                                    key={`${task.hireId}_${task.category}_${task.taskId}`}
	                                    className="tasks-panel-task alert"
	                                    onClick={() => {
	                                      const hire = newHires.find(h => h.id === task.hireId);
	                                      if (hire) {
	                                        selectHireForView(hire);
	                                        setTaskToOpen({ stage: task.category, id: task.taskId });
	                                        if (STAGES.includes(task.category)) {
	                                          setActiveStage(task.category);
	                                        }
	                                      }
	                                    }}
	                                  >
	                                    <span className="tasks-panel-bullet"></span>
	                                    <span>
	                                      {task.title}
	                                      <span className="tasks-panel-assignee">
	                                        {t('tasksPanel.assignedTo', { assignee: task.assigneeLabel })}
	                                      </span>
	                                    </span>
	                                  </button>
	                                ))}
	                              </div>
	                            </div>
	                          ))}
	                        </div>
	                      )}
	                    </>
	                  )}
	                </div>
              </div>
          ) : (
            /* Detail View for Selected Hire */
            <div className="hire-detail-view">
              <header className="hire-compact-header">
              <div className="hire-name-row">
              <button title={t('buttons.backToAllHires')} aria-label={t('buttons.backToAllHires')} className="btn-back-redesigned" onClick={() => selectHireForView(null)}>
                <i className="fa-solid fa-arrow-left"></i>
              </button>

                <h1 className="hire-name-large">{selectedHire.name}</h1>
                <div className="hire-progress-inline" data-tutorial="hire-overview" role="progressbar" aria-label={t('workspace.overall')} aria-valuenow={selectedHireProgress} aria-valuemin={0} aria-valuemax={100} title={t('workspace.overall')}>
                  <span>{t('workspace.overall')}</span>
                  <span>{selectedHireProgress}%</span>
                  <div className="ob-progress-bar-small">
                    <div
                      className="ob-progress-fill"
                      style={{ width: `${selectedHireProgress}%` }}
                    ></div>
                  </div>
                </div>

                {canManageOnboarding && (
                  <details className="hire-more" onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) event.currentTarget.open = false; }} onKeyDown={event => { if (event.key === 'Escape') { event.currentTarget.open = false; event.currentTarget.querySelector('summary')?.focus(); } }}><summary aria-label={t('workspace.more')} title={t('workspace.more')}><i className="fa-solid fa-ellipsis" aria-hidden="true" /></summary>
                  <div className="hire-more-menu">
                    <div className="hire-more-details">
                      <strong>{selectedHire.role}{selectedHire.department && ` · ${getDepartmentLabel(selectedHire.department)}`}</strong>
                      {selectedHire.startDate && <span>{t('workspace.plannedStart', { date: formatDate(getTaskLocalDate(selectedHire.startDate)) })}</span>}
                      {selectedHire.phone && <span><i className="fa-solid fa-phone" aria-hidden="true" /> {selectedHire.phone}</span>}
                    </div>
                    {selectedHire.email && <button type="button" onClick={event => { navigator.clipboard.writeText(selectedHire.email); event.currentTarget.closest('details').open = false; alert(t('alerts.emailCopied')); }}><i className="fa-solid fa-copy" aria-hidden="true" />{t('workspace.copyEmail')}</button>}
                  <button
                    type="button"
                    className="btn-export-hire"
                    onClick={handleExportHireData}
                    disabled={exportingHireData}
                    title={uiText("Export new hire for HRIS")}
                  >
                    <i className="fa-solid fa-file-arrow-down"></i>
                    {exportingHireData ? t('buttons.exportingHire', 'Exporting...') : t('buttons.exportNewHire', 'Export New Hire')}
                  </button>
                  </div>
                  </details>
                )}
              </div>
              </header>

              {/* View Mode Toggle - ONLY keep the toggle buttons, removed task buttons */}
              <div className="hire-work-navigation">
              <div className="view-mode-toggle">
                <button className={`view-mode-btn ${viewMode === 'tasks' ? 'active' : ''}`} aria-pressed={viewMode === 'tasks'} onClick={() => setViewMode('tasks')}>
                  <i className="fa-solid fa-list-check" aria-hidden="true" />
                  {t('workspace.tasks')}
                </button>
                <button
                  className={`view-mode-btn ${viewMode === 'workflow' ? 'active' : ''}`}
                  aria-pressed={viewMode === 'workflow'}
                  onClick={() => setViewMode('workflow')}
                >
                  <i className="fa-solid fa-sitemap"></i>
                  {t('workspace.workflow')}
                </button>
                <button
                  className={`view-mode-btn ${viewMode === 'documents' ? 'active' : ''}`}
                  aria-pressed={viewMode === 'documents'}
                  onClick={() => setViewMode('documents')}
                >
                  <i className="fa-solid fa-folder-open"></i>
                  {t('sections.documentsShared', 'Documents Shared')}
                </button>
              </div>

              {String(selectedHire.status || '').toLowerCase() === 'active' ? (
                <div className="employee-start-confirmed-badge" role="status">
                  <i className="fa-solid fa-circle-check" aria-hidden="true" />
                  {t('labels.employeeStartConfirmed', 'Started on {{date}}', {
                    date: formatDate(getTaskLocalDate(selectedHire.actualStartDate || selectedHire.startDate))
                  })}
                </div>
              ) : canConfirmEmployeeStarted ? (
                <div className="hire-start-status" key={selectedHire.id}>
                  <i className="fa-regular fa-calendar" aria-hidden="true" />
                  <span>{t('workspace.awaitingStart')}</span>
                  <button type="button" onClick={() => startDialogRef.current?.showModal()}>{t('workspace.confirmStart')}</button>
                  <dialog ref={startDialogRef} className="hire-start-dialog" aria-labelledby="hire-start-dialog-title" onCancel={event => { if (confirmingEmployeeStarted) event.preventDefault(); }}>
                    <div className="hire-start-dialog-heading">
                      <h2 id="hire-start-dialog-title">{t('workspace.confirmStart')}</h2>
                      <button type="button" className="hire-start-dialog-close" aria-label={t('buttons.cancel')} disabled={confirmingEmployeeStarted} onClick={() => startDialogRef.current?.close()}><i className="fa-solid fa-xmark" aria-hidden="true" /></button>
                    </div>
                    <p className="hire-start-dialog-name">{selectedHire.name}</p>
                <div className="hire-start-dialog-form">
                  <div>
                    <label htmlFor="employee-actual-start-date">
                      {t('labels.actualStartDate', 'Actual start date')}
                    </label>
                    <span>{t('labels.startConfirmationHelp', 'Confirm only after the employee has actually started. This is separate from onboarding task completion.')}</span>
                  </div>
                  <input
                    id="employee-actual-start-date"
                    type="date"
                    value={actualStartDate}
                    max={localIsoDate()}
                    onChange={(event) => setActualStartDate(event.target.value)}
                    disabled={confirmingEmployeeStarted}
                  />
                  <button
                    type="button"
                    onClick={confirmEmployeeStarted}
                    disabled={confirmingEmployeeStarted || !actualStartDate}
                  >
                    <i className={`fa-solid ${confirmingEmployeeStarted ? 'fa-circle-notch fa-spin' : 'fa-user-check'}`} aria-hidden="true" />
                    {confirmingEmployeeStarted
                      ? t('buttons.confirmingEmployeeStarted', 'Confirming...')
                      : t('buttons.confirmEmployeeStarted', 'Confirm employee started')}
                  </button>
                </div>
                  </dialog>
                </div>
              ) : null}

              </div>

              {/* NEW: Workflow View */}
              {viewMode === 'tasks' ? (
                <HireTaskWorkspace
                  key={selectedHire.id}
                  hire={selectedHire}
                  initialTask={taskToOpen}
                  stages={STAGES}
                  stageLabel={getCategoryLabel}
                  ownerLabel={getHireTaskAssigneeLabel}
                  typeLabel={getHireTaskTypeLabel}
                  automationType={getHireAutomationType}
                  isMine={isTaskAssignedToCurrentUser}
                  canManage={canManageOnboarding && !tutorialActive}
                  canToggle={task => !tutorialActive && canUpdateTaskStatus(task)}
                  onToggle={(task, stage) => updateTaskStatus(selectedHire.id, stage, task.id, task.status === 'completed' ? 'not_started' : 'completed')}
                  onEdit={(task, stage) => { setActiveStage(stage); handleEditTask(task); }}
                  onDelete={(task, stage) => deleteTask(selectedHire.id, stage, task.id)}
                  onAction={handleWorkflowTaskAction}
                  onAdd={(type, stage) => { setActiveStage(stage); setEditingTask(null); type === 'automated' ? setShowAutomatedTaskModal(true) : setShowHumanTaskModal(true); }}
                />
              ) : viewMode === 'workflow' ? (
                <OnboardingWorkflowView
                  hire={selectedHire}
                  stages={STAGES}
                  activeStage={activeStage}
                  onStageChange={setActiveStage}
                  teamMembers={teamMembers}
                  externalContacts={externalContacts}
                  onTaskAction={handleWorkflowTaskAction}
                  onAddTask={handleAddTaskFromWorkflow}
                  onAddAutomatedTask={() => { setEditingTask(null); setShowAutomatedTaskModal(true); }}
                  onAddHumanTask={() => { setEditingTask(null); setShowHumanTaskModal(true); }}
                  onEditTask={handleEditTask}
                  onDeleteTask={handleDeleteWorkflowTask}
                  canManageTasks={canManageOnboarding}
                  canToggleTaskCompletion={(task, nextCompleted) => canUpdateTaskStatus(task, nextCompleted ? 'completed' : 'not_started')}
                />
              ) : viewMode === 'categories' ? (
                <div className="hire-category-template-view" data-tutorial="hire-tasks">
                  <OnboardingStageTimeline
                    stages={STAGES}
                    activeStage={activeStage}
                    onStageClick={setActiveStage}
                    startDate={selectedHire?.startDate}
                    stageProgress={selectedHireStageProgress}
                    onAddAutomatedTask={() => { setEditingTask(null); setShowAutomatedTaskModal(true); }}
                    onAddHumanTask={() => { setEditingTask(null); setShowHumanTaskModal(true); }}
                    canAddTasks={canManageOnboarding}
                  />

                  <div className="template-editor-task-sections hire-category-task-sections">
                    {categoryViewTaskGroups.map(group => {
                      const collapseKey = `hire-category:${activeStage}:${group.key}`;
                      const isExpanded = expandedCategories[collapseKey] !== false;
                      const completedCount = group.tasks.filter(task => task.status === 'completed').length;

                      return (
                        <section
                          key={group.key}
                          className={`template-task-table-section hire-category-task-section ${!isExpanded ? 'is-collapsed' : ''}`}
                        >
                          <div className="template-task-section-header">
                            <button
                              type="button"
                              className="template-section-title-btn"
                              onClick={() => toggleCategory(collapseKey)}
                            >
                              <i className={`fa-solid ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}`} />
                              <span>{uiText(group.label)}</span>
                              <em>{completedCount}/{group.tasks.length}</em>
                            </button>
                          </div>

                          {isExpanded && (
                            <div className="template-team-table-wrapper">
                              <div className="team-table-scroll template-task-table-scroll">
                                <table className="team-table template-task-table hire-category-task-table">
                                  <thead>
                                    <tr>
                                      <th>
                                        <div className="table-header">
                                          <i className="fa-solid fa-user" />
                                          <span>{t('templatesEditor.table.taskName', 'Task Name')}</span>
                                        </div>
                                      </th>
                                      <th>
                                        <div className="table-header">
                                          <i className="fa-solid fa-circle-info" />
                                          <span>{t('templatesEditor.table.taskType', 'Task Type')}</span>
                                        </div>
                                      </th>
                                      <th>
                                        <div className="table-header">
                                          <i className="fa-solid fa-calendar-days" />
                                          <span>{t('templatesEditor.table.timing', 'Timing')}</span>
                                        </div>
                                      </th>
                                      <th>
                                        <div className="table-header">
                                          <i className="fa-solid fa-diagram-project" />
                                          <span>{t('templatesEditor.table.assignedTo', 'Assigned To')}</span>
                                        </div>
                                      </th>
                                      <th>
                                        <div className="table-header">
                                          <i className="fa-solid fa-clock" />
                                          <span>{t('templatesEditor.table.lastUpdated', 'Last Updated')}</span>
                                        </div>
                                      </th>
                                      <th>
                                        <div className="table-header">
                                          <i className="fa-solid fa-ellipsis" />
                                          <span>{t('templatesEditor.table.actions', 'Actions')}</span>
                                        </div>
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {group.tasks.length === 0 ? (
                                      <tr>
                                        <td colSpan={6} className="template-empty-table-cell">
                                          {t('templatesEditor.noTasksInGroup', 'No tasks in this group yet.')}
                                        </td>
                                      </tr>
	                                    ) : group.tasks.map(task => {
	                                      const isComplete = task.status === 'completed';
	                                      const rowMenuKey = `${activeStage}:${group.key}:${task.id}`;
	                                      const automationType = getHireAutomationType(task);
	                                      const nextStatus = isComplete ? 'not_started' : 'completed';
	                                      const canToggleTask = canUpdateTaskStatus(task, nextStatus);

	                                      return (
                                        <tr key={task.id}>
                                          <td>
                                            <div className="hire-category-task-name">
                                              <button
                                                type="button"
                                                disabled={tutorialActive || !canToggleTask}
                                                className={`hire-category-task-check ${isComplete ? 'is-complete' : ''} ${canToggleTask ? '' : 'is-disabled'}`}
                                                onClick={() => updateTaskStatus(
                                                  selectedHire.id,
                                                  activeStage,
                                                  task.id,
                                                  nextStatus
                                                )}
                                                aria-label={isComplete ? t('workflow.clickToUnmarkComplete', 'Click to unmark as complete') : t('workflow.markAsComplete', 'Mark as Complete')}
                                              >
                                                {isComplete && <i className="fa-solid fa-check" />}
                                              </button>
                                              <span className="template-task-name">{task.title || t('templatesEditor.untitledTask', 'Untitled Task')}</span>
                                            </div>
                                          </td>
	                                          <td>
	                                            <span className="template-task-type-pill">
	                                              <i className={`fa-solid ${task.type === 'automated' || automationType ? 'fa-bolt' : 'fa-user-check'}`} />
	                                              {getHireTaskTypeLabel(task)}
	                                            </span>
                                          </td>
                                          <td>{getHireTaskTimingLabel(task)}</td>
                                          <td>{getHireTaskAssigneeLabel(task)}</td>
                                          <td>{getHireTaskUpdatedLabel(task)}</td>
                                          <td>
                                            {canManageOnboarding ? (
                                              <div className="template-inline-menu-wrap">
                                                <button
                                                  type="button"
                                                  className="template-row-menu-btn"
                                                  onClick={(event) => handleCategoryTaskMenuToggle(rowMenuKey, event)}
                                                  aria-label={t('templatesEditor.table.actions', 'Actions')}
                                                >
                                                  <i className="fa-solid fa-ellipsis" />
                                                </button>
                                                {categoryTaskMenu === rowMenuKey && (
                                                  <div className="template-inline-menu template-inline-menu--fixed" style={categoryTaskMenuPosition || undefined}>
                                                    <button
                                                      type="button"
                                                      className="template-inline-menu-item"
                                                      onClick={() => {
                                                        setCategoryTaskMenu(null);
                                                        setCategoryTaskMenuPosition(null);
                                                        handleEditTask(task);
                                                      }}
                                                    >
                                                      <i className="fa-solid fa-pen" />
                                                      {t('templatesEditor.actions.edit', 'Edit')}
                                                    </button>
                                                    <button
                                                      type="button"
                                                      className="template-inline-menu-item"
                                                      onClick={() => {
                                                        setCategoryTaskMenu(null);
                                                        setCategoryTaskMenuPosition(null);
                                                        duplicateHireCategoryTask(activeStage, task);
                                                      }}
                                                    >
                                                      <i className="fa-solid fa-copy" />
                                                      {t('templatesEditor.actions.duplicate', 'Duplicate')}
                                                    </button>
                                                    <button
                                                      type="button"
                                                      className="template-inline-menu-item template-inline-menu-item--danger"
                                                      onClick={() => {
                                                        setCategoryTaskMenu(null);
                                                        setCategoryTaskMenuPosition(null);
                                                        deleteTask(selectedHire.id, activeStage, task.id);
                                                      }}
                                                    >
                                                      <i className="fa-solid fa-trash" />
                                                      {t('templatesEditor.actions.delete', 'Delete')}
                                                    </button>
                                                  </div>
                                                )}
                                              </div>
                                            ) : (
                                              <span className="template-task-readonly-indicator">{t('workflow.viewOnly', 'View only')}</span>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </section>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="hire-documents-shared-section">
                  <div className="hire-documents-shared-header">
                    <h3>{t('sections.documentsShared', 'Documents Shared')}</h3>
                    <span>{t('sections.documentsSharedHint', 'Files uploaded by this employee')}</span>
                  </div>
                  {selectedHireDocumentsLoading ? (
                    <div className="hire-documents-loading">{t('states.loadingDocuments', 'Loading documents...')}</div>
                  ) : (
                    <DocumentManager
                      employeeId={selectedHire.id}
                      companyUID={companyUID}
                      documents={selectedHireDocuments}
                      onRefresh={() => {}}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </FeatureGate>
      </div>

      {/* Placeholder Modal for Contract Templates */}
      {showPlaceholderModal && canManageOnboarding && (
        <div className="modal-overlay placeholder-modal" onClick={() => setShowPlaceholderModal(false)}>
          <div className="modal-content-onboarding" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('modals.placeholder.title')}</h2>
              <div className="close" onClick={() => setShowPlaceholderModal(false)}>&times;</div>
            </div>

            <div className="modal-body-onboarding">
              <p style={{ marginBottom: '20px', color: '#666', fontSize: '14px' }}>
                {t('modals.placeholder.description', { name: selectedHire?.name || '' })}
              </p>

              {detectedPlaceholders.map((placeholder, index) => (
                <div key={index} className="form-group">
                  <label>{placeholder}</label>
                  <input
                    type="text"
                    value={placeholderValues[placeholder] || ''}
                    onChange={(e) => setPlaceholderValues({
                      ...placeholderValues,
                      [placeholder]: e.target.value
                    })}
                    placeholder={t('modals.placeholder.inputPlaceholder', { field: placeholder })}
                  />
                </div>
              ))}
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowPlaceholderModal(false)}>
                {t('common:buttons.cancel')}
              </button>
              <button className="btn-primary" onClick={handlePlaceholderSubmit}>
                {t('buttons.createTask')}
              </button>
            </div>
          </div>
        </div>
      )
      }

      {/* Add Task Modal */}
      {
        showAddTaskModal && canManageOnboarding && (
          <div className="modal-overlay" onClick={() => setShowAddTaskModal(false)}>
            <div className="modal-content-onboarding" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{t('modals.addTask.title', { category: getCategoryLabel(taskCategory) })}</h2>
                <div className="close" onClick={() => setShowAddTaskModal(false)}>&times;</div>
              </div>

              <div className="modal-body-onboarding">
                {/* Task Type Selection */}
                <div className="form-group">
                  <label>{t('modals.addTask.taskType')}</label>
                  <div className="task-type-selector">
                    <button
                      type="button"
                      className={`task-type-btn ${taskForm.type === 'standard' ? 'active' : ''}`}
                      onClick={() => setTaskForm({ ...taskForm, type: 'standard' })}
                    >
                      <i className="fa-solid fa-list-check"></i>
                      <div>
                        <div className="type-title">{t('modals.addTask.standardTitle')}</div>
                        <div className="type-desc">{t('modals.addTask.standardDesc')}</div>
                      </div>
                    </button>
                    <button
                      type="button"
                      className={`task-type-btn ${taskForm.type === 'contract' ? 'active' : ''}`}
                      onClick={() => setTaskForm({ ...taskForm, type: 'contract' })}
                    >
                      <i className="fa-solid fa-file-contract"></i>
                      <div>
                        <div className="type-title">{t('modals.addTask.contractTitle')}</div>
                        <div className="type-desc">{t('modals.addTask.contractDesc')}</div>
                      </div>
                    </button>
                    <button
                      type="button"
                      className={`task-type-btn ${taskForm.type === 'document' ? 'active' : ''}`}
                      onClick={() => setTaskForm({ ...taskForm, type: 'document' })}
                    >
                      <i className="fa-solid fa-file-arrow-up"></i>
                      <div>
                        <div className="type-title">{t('modals.addTask.documentTitle')}</div>
                        <div className="type-desc">{t('modals.addTask.documentDesc')}</div>
                      </div>
                    </button>
                    <button
                      type="button"
                      className={`task-type-btn ${taskForm.type === 'documentRequest' ? 'active' : ''}`}
                      onClick={() => setTaskForm({ ...taskForm, type: 'documentRequest' })}
                    >
                      <i className="fa-solid fa-file-import"></i>
                      <div>
                        <div className="type-title">{t('modals.addTask.documentRequestTitle') || 'Document Request'}</div>
                        <div className="type-desc">{t('modals.addTask.documentRequestDesc') || 'Request docs from new hire'}</div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Task Title */}
                <div className="form-group">
                  <label>{t('modals.addTask.taskTitle')}</label>
                  <input
                    type="text"
                    value={taskForm.title}
                    onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                    placeholder={t('modals.addTask.taskTitlePlaceholder')}
                  />
                </div>

                {/* Task Description */}
                <div className="form-group">
                  <label>{t('modals.addTask.description')}</label>
                  <textarea
                    value={taskForm.description}
                    onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                    placeholder={t('modals.addTask.descriptionPlaceholder')}
                    rows={3}
                  />
                </div>

                {/* Contract Template Selection */}
                {taskForm.type === 'contract' && (
                  <div className="form-group">
                    <label>{t('modals.addTask.contractTemplate')}</label>
                    <select
                      value={taskForm.contractTemplateId}
                      onChange={(e) => setTaskForm({ ...taskForm, contractTemplateId: e.target.value })}
                    >
                      <option value="">{t('modals.addTask.contractTemplateEmpty')}</option>
                      {loadingTemplates ? (
                        <option disabled>{t('modals.addTask.loadingTemplates')}</option>
                      ) : (
                        contractTemplates.map(template => (
                          <option key={template.id} value={template.id}>
                            {template.title || t('modals.addTask.untitledTemplate')}
                          </option>
                        ))
                      )}
                    </select>
                    {contractTemplates.length === 0 && !loadingTemplates && (
                      <small style={{ color: '#666', marginTop: '8px', display: 'block' }}>
                        {t('modals.addTask.noContractTemplates')}
                      </small>
                    )}
                  </div>
                )}

                {/* Document Upload */}
                {taskForm.type === 'document' && (
                  <div className="form-group">
                    <label>{t('modals.addTask.uploadDocument')}</label>
                    <div className="file-upload-area">
                      <input
                        type="file"
                        id="task-file-upload"
                        accept=".pdf,.doc,.docx"
                        onChange={handleTaskFileUpload}
                        style={{ display: 'none' }}
                      />
                      <label htmlFor="task-file-upload" className="file-upload-label">
                        <i className="fa-solid fa-cloud-arrow-up"></i>
                        {taskForm.uploadedFile ? (
                          <div>
                            <div style={{ fontWeight: 600 }}>{t('modals.addTask.fileSelected')}</div>
                            <div>{taskForm.uploadedFile.name}</div>
                          </div>
                        ) : (
                          <div>
                            <div style={{ fontWeight: 600 }}>{t('modals.addTask.uploadCta')}</div>
                            <div style={{ fontSize: '12px', opacity: 0.7 }}>{t('modals.addTask.uploadFormats')}</div>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label>{t('modals.addTask.requiredDocuments') || 'Required Documents (optional)'}</label>
                  <textarea
                    value={taskForm.requiredDocumentsText}
                    onChange={(e) => setTaskForm({ ...taskForm, requiredDocumentsText: e.target.value })}
                    placeholder={t('modals.addTask.requiredDocumentsPlaceholder') || 'One document per line, e.g.\nGovernment ID\nTax Form'}
                    rows={3}
                  />
                  <small style={{ color: '#64748b', marginTop: '4px', display: 'block' }}>
                    {t('modals.addTask.requiredDocumentsHint') || 'Candidate uploads one file per listed document. Task can auto-complete once all are uploaded.'}
                  </small>
                </div>

                {/* Due Date */}
                <div className="form-group">
                  <label>{t('modals.addTask.dueDate')}</label>
                  <input
                    type="date"
                    value={taskForm.dueDate}
                    onChange={(e) => setTaskForm({ ...taskForm, dueDate: e.target.value })}
                  />
                </div>

                {/* Unified Multi-Select Assignees Dropdown */}
                <div className="form-group">
                  <label>{t('modals.addTask.assignTo') || 'Assign To'}</label>

                  {/* Multi-select dropdown with all options */}
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value && !taskForm.assignees.includes(e.target.value)) {
                        setTaskForm({ ...taskForm, assignees: [...taskForm.assignees, e.target.value] });
                      }
                    }}
                    className="assignee-select"
                  >
                    <option value="">{t('modals.addTask.selectAssignee') || 'Select assignee...'}</option>

                    {/* New Hire option */}
                    {selectedHire?.email && !taskForm.assignees.includes(selectedHire.email) && (
                      <option value={selectedHire.email}>
                        👤 {selectedHire.name} ({t('workflow.newHire', 'New Hire')})
                      </option>
                    )}

                    {/* Manager option */}
                    {selectedHire?.manager && !taskForm.assignees.includes(selectedHire.manager) && (
                      <option value={selectedHire.manager}>
                        👔 {teamMembers.find(m => m.email === selectedHire.manager)?.name || selectedHire.manager} ({t('workflow.manager', 'Manager')})
                      </option>
                    )}

                    {/* IT team members */}
                    {teamMembers.filter(m => m.role === 'IT' && !taskForm.assignees.includes(m.email)).length > 0 && (
                      <optgroup label={`🖥️ ${t('workflow.itTeam', 'IT Team')}`}>
                        {teamMembers.filter(m => m.role === 'IT' && !taskForm.assignees.includes(m.email)).map(member => (
                          <option key={member.email} value={member.email}>
                            {member.name}
                          </option>
                        ))}
                      </optgroup>
                    )}

                    {/* Other team members */}
                    {teamMembers.filter(m =>
                      m.role !== 'IT' &&
                      m.email !== selectedHire?.email &&
                      m.email !== selectedHire?.manager &&
                      !taskForm.assignees.includes(m.email)
                    ).length > 0 && (
                        <optgroup label={`👥 ${t('workflow.teamMembers', 'Team Members')}`}>
                          {teamMembers.filter(m =>
                            m.role !== 'IT' &&
                            m.email !== selectedHire?.email &&
                            m.email !== selectedHire?.manager &&
                            !taskForm.assignees.includes(m.email)
                          ).map(member => (
                            <option key={member.email} value={member.email}>
                              {member.name} {member.role ? `(${member.role})` : ''}
                            </option>
                          ))}
                        </optgroup>
                      )}

                    {/* External Contacts */}
                    {externalContacts && externalContacts.filter(c => !taskForm.assignees.includes(c.email)).length > 0 && (
                      <optgroup label={`🔗 ${t('externalContacts.title', 'External Team')}`}>
                        {externalContacts.filter(c => !taskForm.assignees.includes(c.email)).map(contact => (
                          <option key={contact.id} value={contact.email}>
                            {contact.name || contact.email} ({contact.role || t('workflow.externalContact', 'External')})
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>

                  {/* Display selected assignees as chips */}
                  {taskForm.assignees.length > 0 && (
                    <div className="assignee-chips" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
                      {taskForm.assignees.map(email => {
                        const member = teamMembers.find(m => m.email === email);
                        const isNewHire = email === selectedHire?.email;
                        const isManager = email === selectedHire?.manager;
                        const isIT = member?.role === 'IT';

                        let label = member?.name || email;
                        let bgColor = '#e3f2fd';
                        let textColor = '#1976d2';

                        if (isNewHire) {
                          label = `${selectedHire.name} (${t('workflow.newHire', 'New Hire')})`;
                          bgColor = '#e8f5e9';
                          textColor = '#388e3c';
                        } else if (isManager) {
                          label = `${member?.name || email} (${t('workflow.manager', 'Manager')})`;
                          bgColor = '#fff3e0';
                          textColor = '#f57c00';
                        } else if (isIT) {
                          label = `${member?.name || email} (${t('workflow.it', 'IT')})`;
                          bgColor = '#f3e5f5';
                          textColor = '#7b1fa2';
                        }

                        return (
                          <span
                            key={email}
                            className="assignee-chip"
                            style={{
                              background: bgColor,
                              color: textColor,
                              padding: '4px 10px',
                              borderRadius: '16px',
                              fontSize: '13px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                          >
                            {uiText(label)}
                            <button
                              type="button"
                              onClick={() => setTaskForm({
                                ...taskForm,
                                assignees: taskForm.assignees.filter(a => a !== email)
                              })}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '0',
                                color: textColor,
                                fontSize: '14px',
                                lineHeight: 1
                              }}
                              data-light-control={"quiet"}>
                              ×
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Scheduled Email Section */}
                <div className="form-group">
                  <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={taskForm.includeEmail}
                      onChange={(e) => setTaskForm({ ...taskForm, includeEmail: e.target.checked })}
                    />
                    <span>{t('modals.addTask.scheduleEmail') || 'Schedule an email with this task'}</span>
                  </label>
                </div>

                {taskForm.includeEmail && (
                  <div className="email-config-section" style={{ background: '#f8f9fa', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
                    <div className="form-group">
                      <label>{t('modals.addTask.emailRecipients') || 'Email Recipients'}</label>
                      <select
                        value={taskForm.emailConfig.recipients}
                        onChange={(e) => setTaskForm({
                          ...taskForm,
                          emailConfig: { ...taskForm.emailConfig, recipients: e.target.value }
                        })}
                      >
                        <option value="newHire">{t('modals.addTask.recipientNewHire') || 'New Hire'}</option>
                        <option value="assignees">{t('modals.addTask.recipientAssignees') || 'All Assignees'}</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>{t('modals.addTask.emailSubject') || 'Subject'}</label>
                      <input
                        type="text"
                        value={taskForm.emailConfig.subject}
                        onChange={(e) => setTaskForm({
                          ...taskForm,
                          emailConfig: { ...taskForm.emailConfig, subject: e.target.value }
                        })}
                        placeholder={t('modals.addTask.emailSubjectPlaceholder') || 'Email subject...'}
                      />
                    </div>

                    <div className="form-group">
                      <label>{t('modals.addTask.emailBody') || 'Message'}</label>
                      <OnboardingQuillEditor
                        className="onboarding-quill"
                        value={taskForm.emailConfig.body}
                        onChange={(value) => setTaskForm({
                          ...taskForm,
                          emailConfig: { ...taskForm.emailConfig, body: value }
                        })}
                        placeholder={t('modals.addTask.emailBodyPlaceholder') || 'Email message...'}
                      />
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => generateEmailWithAI()}
                        style={{ marginTop: '8px' }}
                      >
                        <i className="fa-solid fa-wand-magic-sparkles"></i>
                        {' '}{t('modals.addTask.generateWithAI') || 'Generate with AI'}
                      </button>
                    </div>

                    <div className="form-group">
                      <label>{t('modals.addTask.sendAt') || 'Send At'}</label>
                      <input
                        type="datetime-local"
                        value={taskForm.emailConfig.scheduledFor}
                        onChange={(e) => setTaskForm({
                          ...taskForm,
                          emailConfig: { ...taskForm.emailConfig, scheduledFor: e.target.value }
                        })}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setShowAddTaskModal(false)}>
                  {t('common:buttons.cancel')}
                </button>
                <button className="btn-primary" onClick={handleCreateTask}>
                  {t('buttons.createTask')}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Add New Hire Modal */}
      {
        showAddHireModal && canManageOnboarding && (
          <div className="modal-overlay" onClick={() => setShowAddHireModal(false)}>
            <div className="modal-content-onboarding" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{t('modals.addHire.title')}</h2>
                <div className="close" onClick={() => setShowAddHireModal(false)}>&times;</div>
              </div>

              <div className="modal-body-onboarding">
                <div className="form-group">
                  <label>{t('modals.addHire.fullName')}</label>
                  <input
                    type="text"
                    value={newHireForm.name}
                    onChange={(e) => setNewHireForm({ ...newHireForm, name: e.target.value })}
                    placeholder={t('modals.addHire.namePlaceholder')}
                  />
                </div>

                <div className="form-group">
                  <label>{t('modals.addHire.email')}</label>
                  <input
                    type="email"
                    value={newHireForm.email}
                    onChange={(e) => setNewHireForm({ ...newHireForm, email: e.target.value })}
                    placeholder={t('modals.addHire.emailPlaceholder')}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('modals.addHire.role')}</label>
                    <input
                      type="text"
                      value={newHireForm.role}
                      onChange={(e) => setNewHireForm({ ...newHireForm, role: e.target.value })}
                      placeholder={t('modals.addHire.rolePlaceholder')}
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('modals.addHire.department')}</label>
                    <select
                      value={newHireForm.department}
                      onChange={(e) => setNewHireForm({ ...newHireForm, department: e.target.value })}
                    >
                      <option value="">{t('modals.addHire.departmentPlaceholder')}</option>
                      {departmentOptionsList.map(option => (
                        <option key={option.value} value={option.value}>
                          {uiText(option.label)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('modals.addHire.startDate')}</label>
                  <input
                    type="date"
                    value={newHireForm.startDate}
                    onChange={(e) => setNewHireForm({ ...newHireForm, startDate: e.target.value })}
                  />
                </div>

                {nationalizationEstablishments.length > 1 && (
                  <div className="form-group">
                    <label>{t('modals.addHire.legalEstablishment', 'Legal company / branch')}</label>
                    <select
                      value={newHireEstablishmentId}
                      onChange={(event) => setNewHireEstablishmentId(event.target.value)}
                      required
                    >
                      <option value="">{t('modals.addHire.selectLegalEstablishment', 'Select a legal company or branch')}</option>
                      {nationalizationEstablishments.map((establishment) => (
                        <option key={establishment.id} value={establishment.id}>
                          {establishment.name || establishment.id}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label>{t('modals.addHire.manager')}</label>
                  <input
                    type="text"
                    list="manager-suggestions"
                    value={newHireForm.manager}
                    onChange={(e) => setNewHireForm({ ...newHireForm, manager: e.target.value })}
                    placeholder={t('modals.addHire.managerPlaceholder')}
                  />
                  <datalist id="manager-suggestions">
                    {teamMembers.map(member => (
                      <option key={member.email} value={member.email}>
                        {member.name}
                      </option>
                    ))}
                  </datalist>
                </div>

                <div className="form-group">
                  <label>{t('modals.addHire.buddy')}</label>
                  <input
                    type="text"
                    list="buddy-suggestions"
                    value={newHireForm.buddyMentor}
                    onChange={(e) => setNewHireForm({ ...newHireForm, buddyMentor: e.target.value })}
                    placeholder={t('modals.addHire.buddyPlaceholder')}
                  />
                  <datalist id="buddy-suggestions">
                    {teamMembers.map(member => (
                      <option key={member.email} value={member.email}>
                        {member.name}
                      </option>
                    ))}
                  </datalist>
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setShowAddHireModal(false)}>
                  {t('common:buttons.cancel')}
                </button>
                <button className="btn-primary" onClick={handleAddHire}>
                  {t('buttons.addNewHire')}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Email Preview/Edit Modal */}
      {
        showEmailPreview && (
          <div className="modal-overlay" onClick={() => setShowEmailPreview(false)}>
            <div className="modal-content-onboarding" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{t('modals.emailPreview.title')}</h2>
                <div className="close" onClick={() => setShowEmailPreview(false)}>&times;</div>
              </div>

              <div className="modal-body-onboarding">
                <div className="form-group">
                  <label>{t('modals.emailPreview.to')}</label>
                  <input
                    type="email"
                    value={emailDraft.to}
                    onChange={(e) => setEmailDraft({ ...emailDraft, to: e.target.value })}
                    placeholder={t('modals.emailPreview.toPlaceholder')}
                  />
                  <small style={{ display: 'block', marginTop: '4px', color: '#666' }}>
                    {emailDraft.toName}
                  </small>
                </div>

                <div className="form-group">
                  <label>{t('modals.emailPreview.subject')}</label>
                  <input
                    type="text"
                    value={emailDraft.subject}
                    onChange={(e) => setEmailDraft({ ...emailDraft, subject: e.target.value })}
                    placeholder={t('modals.emailPreview.subjectPlaceholder')}
                  />
                </div>

                <div className="form-group">
                  <label>{t('modals.emailPreview.message')}</label>
                  <OnboardingQuillEditor
                    className="onboarding-quill"
                    value={emailDraft.message}
                    onChange={(value) => setEmailDraft({ ...emailDraft, message: value })}
                    placeholder={t('modals.emailPreview.messagePlaceholder')}
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setShowEmailPreview(false)}>
                  {t('common:buttons.cancel')}
                </button>
                <button className="btn-primary" onClick={handleSendEmail}>
                  <i className="fa-solid fa-paper-plane"></i> {t('modals.emailPreview.send')}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* NEW: Automated Task Modal */}
      {
        showAutomatedTaskModal && selectedHire && canManageOnboarding && (
          <AddAutomatedTaskModal
            onClose={() => { setShowAutomatedTaskModal(false); setEditingTask(null); }}
            onSave={handleSaveAutomatedTasks}
            hire={selectedHire}
            stage={activeStage}
            editingTask={editingTask}
          />
        )
      }

      {/* NEW: Manual Task Modal */}
      {
        showHumanTaskModal && selectedHire && canManageOnboarding && (
          <AddHumanTaskModal
            onClose={() => { setShowHumanTaskModal(false); setEditingTask(null); }}
            onSave={handleSaveHumanTask}
            hire={selectedHire}
            stage={activeStage}
            editingTask={editingTask}
            teamMembers={teamMembers}
            externalContacts={externalContacts}
          />
        )
      }

      {/* External Contacts Modal */}
      <ExternalContactsModal
        isOpen={showContactsModal}
        onClose={() => setShowContactsModal(false)}
        contacts={externalContacts}
        onAdd={handleAddExternalContact}
        onRemove={handleRemoveExternalContact}
        t={t}
      />

      {/* Template Manager Modal */}
      <TemplateManagerModal
        isOpen={showTemplatesModal}
        onClose={() => setShowTemplatesModal(false)}
        companyUID={companyUID}
        teamMembers={teamMembers}
        externalContacts={externalContacts}
        onSaveExternalContact={handleSaveExternalContact}
        onRemoveExternalContact={handleRemoveExternalContact}
        canManageTemplates={canManageOnboarding}
        t={t}
      />

    </div >
  );
};

export default Onboarding;
